import React from 'react';
import {
  ExternalLink,
  Icon,
  BadgeStuff,
  BadgeBetaTester,
  BadgeTopReviewer,
  BadgeTier,
} from '../../components';
import config from '../../config';

import css from './ArticlePage/ArticlePage.module.css';

const PROFILE_AND_ACCOUNT_SETTINGS = 'profile-and-account-settings';
const LIVE_ROOM = 'live-room';
const SHOW_PAGE = 'show-page';
const PAYMENTS_AND_REFUNDS = 'payments-and-refunds';
const BECOMING_A_CREATOR = 'becoming-a-creator';
const GROW_YOUR_COMMUNITY = 'grow-your-community';
const CREATOR_DASHBOARD = 'creator-dashboard';
const GETTING_PAID = 'getting-paid';

const englishTopics = [
  {
    key: PROFILE_AND_ACCOUNT_SETTINGS,
    label: 'Profile and Account Settings',
    description: 'Manage your profile and account settings',
    icon: 'person',
  },
  {
    key: LIVE_ROOM,
    label: 'Live Room',
    description: 'Learn about Live Room features and settings',
    icon: 'video',
  },
  {
    key: SHOW_PAGE,
    label: 'Production Page',
    description: 'Learn about Production Page features and settings',
    icon: 'document',
  },
  {
    key: PAYMENTS_AND_REFUNDS,
    label: 'Payments and Refunds',
    description: 'Manage payments and refunds',
    icon: 'payment',
  },
  {
    key: BECOMING_A_CREATOR,
    label: 'Become a Creator',
    description: 'Learn how to become a Creator',
    icon: 'star',
  },
  {
    key: GROW_YOUR_COMMUNITY,
    label: 'Grow Your Community',
    description: 'Learn how to grow your community',
    icon: 'users',
  },
  {
    key: CREATOR_DASHBOARD,
    label: 'Creator Dashboard',
    description: 'Manage your creator dashboard',
    icon: 'dashboard',
  },
  {
    key: GETTING_PAID,
    label: 'Getting paid',
    description: 'Manage payments and learn how to get paid',
    icon: 'money',
  },
];

const frenchTopics = [
  {
    key: PROFILE_AND_ACCOUNT_SETTINGS,
    label: 'Profil et Paramètres du Compte',
    description: 'Gérez votre profil et vos paramètres de compte',
    icon: 'person',
  },
  {
    key: LIVE_ROOM,
    label: 'Salle en Direct',
    description: 'Découvrez les fonctionnalités et paramètres des Salles en Direct',
    icon: 'video',
  },
  {
    key: SHOW_PAGE,
    label: 'Page de Spectacle',
    description: 'Découvrez les fonctionnalités et paramètres des Pages de Spectacle',
    icon: 'document',
  },
  {
    key: PAYMENTS_AND_REFUNDS,
    label: 'Paiements et Remboursements',
    description: 'Gérez vos paiements et remboursements',
    icon: 'payment',
  },
  {
    key: BECOMING_A_CREATOR,
    label: 'Devenir Créateur',
    description: 'Découvrez comment devenir un Créateur',
    icon: 'star',
  },
  {
    key: GROW_YOUR_COMMUNITY,
    label: 'Développez Votre Communauté',
    description: 'Apprenez comment développer votre communauté',
    icon: 'users',
  },
  {
    key: CREATOR_DASHBOARD,
    label: 'Tableau de Bord Créateur',
    description: 'Gérez votre tableau de bord créateur',
    icon: 'dashboard',
  },
  {
    key: GETTING_PAID,
    label: 'Recevoir des Paiements',
    description: 'Gérez vos paiements et apprenez comment être payé',
    icon: 'money',
  },
];

const topics = {
  en: englishTopics,
  fr: frenchTopics,
};

const englishArticles = [
  {
    key: 'creating-an-account',
    label: 'Creating an Account',
    topic: PROFILE_AND_ACCOUNT_SETTINGS,
    Html: (
      <div>
        <h3 className={css.noTopMargin}>Creating an Account</h3>
        <p>
          Creating an Attractr account is free and easy. Just visit{' '}
          <ExternalLink href="https://beta.attractr.live">beta.attractr.live</ExternalLink>, click
          Sign Up, and follow the step-by-step instructions.
        </p>
        <h3>Choose profile type</h3>
        <p>
          When signing up, choose your profile type: Audience or Creator. The Creator profile is
          designed for production companies, artists, creators, and marketers who want to create and
          manage a Production Page and Live Room, as well as access the Creator Dashboard. If you
          select a Creator profile, you can easily switch between Creator and Audience modes at any
          time without needing to create a new account. If you're not planning to create a
          Production Page or Live Room, simply choose the Audience profile.
        </p>
        <h3>Sign-up</h3>
        <p>You can sign up using any of the following options:</p>
        <ul>
          <li>Email address</li>
          <li>Facebook account</li>
          <li>Google account</li>
        </ul>
        <p>
          Once you’ve completed the sign-up process, you will receive an Email Verification in your
          registered inbox. Be sure to click the verification link within 48 hours to confirm your
          email. If you don’t verify within that timeframe, the link will expire. However, you can
          easily request a new one by clicking Resend verification email on the login page.
        </p>
        <p>
          After signing up, you’ll need to provide additional information before you can fully
          access all features such as joining memberships, booking Live Room events, or contacting
          Creators. To complete your account setup, go to the Account section.
        </p>
        <p>At a minimum, we’ll need:</p>
        <ul>
          <li>Full name</li>
          <li>Email address</li>
          <li>Profile picture</li>
          <li>Phone number</li>
          <li>Date of birth</li>
          <li>Location</li>
          <li>Payment information</li>
        </ul>
        <h3>Public Account Information</h3>
        <p>
          Your private information is managed in line with our Privacy Policy, ensuring it’s not
          shared with Creators or guests. The only details visible on your public profile are:
        </p>
        <ul>
          <li>First name</li>
          <li>Verification status (Email and Phone)</li>
        </ul>
        <p>Rest assured, your payment information is completely private and only visible to you.</p>
      </div>
    ),
  },
  {
    key: 'change-your-password',
    label: 'Change Your Password',
    topic: PROFILE_AND_ACCOUNT_SETTINGS,
    Html: (
      <div>
        <h3 className={css.noTopMargin}>Change Your Password</h3>
        <p>
          Forgot your password or having trouble logging in? Here’s how you can change or reset your
          password.
        </p>
        <h3>How to Change Your Password:</h3>
        <ul>
          <li>Go to the Account section and select Password Settings.</li>
          <li>Enter a new password.</li>
          <li>To complete the change, enter your current password*.</li>
          <li>Click Save Changes.</li>
        </ul>
        <h3>Reset Your Password:</h3>
        <p>If you don't remember your current password:</p>
        <ul>
          <li>Click Send reset instructions.</li>
          <li>You’ll receive an email from Attractr with password reset instructions.</li>
          <li>Click the link in the email to add a new password.</li>
          <li>Click Reset Password to finalize the process.</li>
        </ul>
      </div>
    ),
  },
  {
    key: 'changing-your-email-address',
    label: 'Changing Your Email Address',
    topic: PROFILE_AND_ACCOUNT_SETTINGS,
    Html: (
      <div>
        <h3 className={css.noTopMargin}>Changing Your Email Address</h3>
        <p>
          You can easily change your email address associated with Attractr. Please note that each
          email address can only be linked to one Attractr account at a time.
        </p>
        <h3>How to Change Your Email Address:</h3>
        <ul>
          <li>Go to the Account section and select Contact Details.</li>
          <li>Enter your new email address in the Your email address field.</li>
          <li>Enter your current password for security verification.</li>
          <li>Click Save Changes.</li>
          <li>Select Resend verification email.</li>
          <li>
            Open the email from Attractr and click the verification link to confirm your new email
            address.
          </li>
        </ul>
        <p>
          Important: Your new email address will not be updated until you complete the verification
          process.
        </p>
      </div>
    ),
  },
  {
    key: 'create-and-edit-your-profile',
    label: 'Create and Edit Your Profile',
    topic: PROFILE_AND_ACCOUNT_SETTINGS,
    Html: (
      <div>
        <h3 className={css.noTopMargin}>Create and Edit Your Profile</h3>
        <p>
          At Attractr, we believe in fostering authentic and meaningful connections between Creators
          and Audiences. To help build those relationships, take a moment to complete your profile
          with key details.
        </p>
        <h3>How to Complete Your Profile:</h3>
        <ul>
          <li>Add your Full Name.</li>
          <li>Write a brief Bio.</li>
          <li>Include your Location.</li>
          <li>Select your Language(s).</li>
          <li>Add your Instagram handle link.</li>
        </ul>
        <p>Don’t forget to upload a Profile Picture to personalize your account.</p>
        <h3>Profile Picture Guidelines:</h3>
        <p>We highly recommend all members upload a profile photo. For the best quality:</p>
        <ul>
          <li>Your picture should be at least 320 pixels wide and 320 pixels tall.</li>
          <li>The image will be cropped into a circle.</li>
          <li>
            For optimal results, we suggest using a PNG file, though JPEG files are also supported.
          </li>
        </ul>
        <h3>What Will Be Displayed on Your Profile:</h3>
        <h4>For Audience</h4>
        <ul>
          <li>User badges</li>
          <li>Total engagement scores (Stats)</li>
          <li>Reviews</li>
          <li>Watchlists</li>
          <li>Top engaging artists this month</li>
          <li>Following</li>
        </ul>
        <h4>For Creators</h4>
        <ul>
          <li>Bio</li>
          <li>Personal details</li>
          <li>Production Credits</li>
          <li>Live Rooms</li>
          <li>People frequently working with</li>
        </ul>
      </div>
    ),
  },
  {
    key: 'user-badge',
    label: 'User Badge',
    topic: PROFILE_AND_ACCOUNT_SETTINGS,
    Html: (
      <div>
        <h3 className={css.noTopMargin}>User Badge</h3>
        <p>Visual indicators awarded to members for achievements or roles within the community.</p>
        <h3>What Are User Badges in Attractr?</h3>
        <p>
          User Badges on Attractr are a form of recognition for members' active participation and
          engagement in our vibrant community. These badges are designed to reward and encourage
          positive interactions, such as rating and reviewing productions, leaving comments, and
          participating in Live Room activities, including making donations and subscribing to
          Creators.
        </p>
        <p>Badges can be earned for various actions and achievements, such as:</p>
        <ul>
          <li>Completing a certain number of ratings and reviews.</li>
          <li>Subscribing to a specific number of Live Room events.</li>
          <li>Contributing through donations to Creators.</li>
        </ul>
        <p>
          Badges not only add a fun and engaging way to motivate members to participate, but they
          also help identify active and valuable contributors within the community. Displayed on
          your profile, badges introduce a gamification element to the platform, promoting a sense
          of achievement. They foster community spirit, encourage positive behavior, and boost
          overall engagement.
        </p>
        <h3>How to Earn User Badges:</h3>
        <p>To earn badges, actively participate in the community! Here are some ways to start:</p>
        <ul>
          <li>Rate and review productions.</li>
          <li>Subscribe to Live Rooms and support Creators.</li>
          <li>Invite friends to join Live Room events.</li>
        </ul>
        <p>
          The more you contribute and engage, the more recognition you’ll receive—from both the
          community and Creators.
        </p>
        <h3>Point system</h3>
        <ul>
          <li>Live Room subscription spend: $1 = 10 points</li>
          <li>Donation spend: $1 = 10 points</li>
          <li># of Reviews and rating made: 1 review = 20 points, 1 rating = 5 points</li>
          <li># of Live Room Episode joined: 1 Live Room episode = 10 points</li>
          <li># of comments on Live Room: 1 comment = 5 points</li>
          <li># of friends invited for Live Room: 1 friend invited = 10 points</li>
        </ul>
        <h3>Tier type:</h3>
        <ul>
          {Object.values(config.userTiers).map(tier => {
            const tierName = tier.id.charAt(0).toUpperCase() + tier.id.slice(1);
            return (
              <li key={tier.id}>
                <BadgeTier className={css.badgeIcon} tierId={tier.id} title={tierName} size={24} />{' '}
                <strong>{tierName}:</strong> {tier.min}
                {tier.max === Infinity ? '+' : ` - ${tier.max}`} points
              </li>
            );
          })}
        </ul>
        <h3>Special Badges</h3>
        <ul>
          <li>
            <BadgeStuff className={css.badgeIcon} /> <strong>Attractr staff:</strong> Our team.
          </li>
          <li>
            <BadgeBetaTester className={css.badgeIcon} /> <strong>Best tester:</strong> Awarded to
            outstanding beta testers who help improve Attractr through valuable feedback and
            participation.
          </li>
          <li>
            <BadgeTopReviewer className={css.badgeIcon} /> <strong>Top reviewer:</strong> Ranked in
            the top 1% of Attractr members based on the number of reviews contributed.
          </li>
        </ul>
      </div>
    ),
  },
  {
    key: 'creator-profile',
    label: 'Creator Profile',
    topic: PROFILE_AND_ACCOUNT_SETTINGS,
    Html: (
      <div>
        <h3 className={css.noTopMargin}>Creator Profile</h3>
        <p>
          When you register as a Creator, you will have two types of profiles: an Audience Profile
          and a Creator Profile. Your Creator Profile serves as a portfolio showcasing your work. It
          accumulates all the production credits you've been given or added to, providing a
          comprehensive display of your contributions and achievements across various projects. This
          profile allows audiences and industrial professionals to explore your creative journey and
          engage with your work.
        </p>
        <h3>Access to your Creator Profile:</h3>
        <p>
          To view your Creator Profile, simply click on your profile in the credit section of any
          Live Room or Production Page. Currently, direct editing is unavailable, but we’re actively
          working to expand these features for enhanced customization soon.
        </p>
      </div>
    ),
  },
  {
    key: 'deleting-your-account',
    label: 'Deleting Your Account',
    topic: PROFILE_AND_ACCOUNT_SETTINGS,
    Html: (
      <div>
        <h3 className={css.noTopMargin}>Deleting Your Account</h3>
        <p>
          As we build a comprehensive knowledge database for the performing arts, we’re carefully
          designing the account deletion process to ensure that public information linked to
          Production Pages remains preserved and secure. Once finalized, this feature will be
          integrated into the platform. In the meantime, if you wish to delete your account, please
          reach out to us at <a href="mailto:team@attractr.live">team@attractr.live</a> to request
          account removal.
        </p>
      </div>
    ),
  },
  {
    key: 'what-is-a-live-room',
    label: 'What is a Live Room?',
    topic: LIVE_ROOM,
    Html: (
      <div>
        <h3 className={css.noTopMargin}>What is a Live Room?</h3>
        <p>
          A Live Room is an interactive virtual space where Creators—including artists, production
          teams, and craftsmen—invite audiences to experience the creative process behind their
          productions. It’s your chance to step beyond the stage and get exclusive access to
          behind-the-scenes content, live streams, and direct interactions with the creators.
          Whether it’s offering input to shape a project, enjoying a casual post-performance chat,
          or watching live rehearsals, Live Rooms bring you closer to the heart of the production.
        </p>
        <p>
          Live Rooms offer both free and membership-based liveRooms, with events scheduled in
          advance. You can easily join via desktop, tablet, or mobile, and immerse yourself in the
          untold stories behind the productions, all from the comfort of your own space.
        </p>
        <h3>Enrich Your Ticket Buying Journey:</h3>
        <p>
          Live Rooms are featured on a production's Production Page, allowing you to explore the
          project more deeply before deciding to attend in person. It’s not just about learning
          more—it’s about forging a personal connection with the artists and creators, discovering
          their journeys, and supporting them directly. By becoming a member, you can help bring
          these creative visions to life while enjoying unique perks and content that go far beyond
          what a traditional event page offers.
        </p>
      </div>
    ),
  },
  {
    key: 'booking-live-room-episodes',
    label: 'Booking Live Room Episodes',
    topic: LIVE_ROOM,
    Html: (
      <div>
        <h3 className={css.noTopMargin}>Booking Live Room Episodes</h3>
        <p>
          A Live Room is a dedicated space where Creators—such as artists, production teams, or
          craftsmen—share behind-the-scenes content, engage in live interactions, or host post-show
          hangouts.
        </p>
        <h3>How to Book and Attend a Live Room Episode:</h3>
        <ul>
          <li>Choose an Episode: Select the episode you’d like to attend and click RSVP.</li>
          <li>
            Join for Free: You’ll need to become a free member to book any free Live Room episodes.
            Click Join For Free to continue.
          </li>
          <li>
            Add a Personal Message (Optional): On the next page, you can write a personal message to
            the Creator before clicking Confirm Booking.
          </li>
          <li>Receive Your Episode Link: A link to the episode will be sent to your inbox.</li>
          <li>Attend the Episode: Click the link to join the Live Room episode.</li>
        </ul>
        <p>Your booked episodes will also be added to your calendar for easy access.</p>
        <p>
          We use Whereby for Live Room episodes, accessible via desktop, tablet, or mobile by
          clicking the episode link ("Launch in web"). The Whereby app is available for iOS.
        </p>
        <p>
          Episodes marked with a star (<Icon name="premium" size={16} className={css.star} />) are
          members-only. For more details, see How to Subscribe to Live Room Membership.
        </p>
      </div>
    ),
  },
  {
    key: 'booking-live-room-episodes-for-groups-or-guests',
    label: 'Booking Live Room Episodes for Groups or Guests',
    topic: LIVE_ROOM,
    Html: (
      <div>
        <h3>Free Live Room Episodes</h3>
        <p>
          You can share free Live Room episodes with your family, friends, or classmates. Each
          episode can accommodate up to 100 audience members, and you can invite up to 10 guests
          when making a booking.
        </p>
        <h3>How to Book Live Room Episodes for Groups or Guests:</h3>
        <ul>
          <li>Click Guests on the schedule panel and select the group size.</li>
          <li>Choose an episode and click RSVP.</li>
          <li>Optionally, write a personal message to the Creator and click Confirm Booking.</li>
          <li>
            On the confirmation screen, click Add This Episode to Your Calendar and invite your
            guests through the calendar invite.
          </li>
          <li>Your guests will receive a Whereby link to join the Live Room episode.</li>
        </ul>
        <p>
          <strong>Note:</strong> All guests must sign up for Attractr to access the Live Room
          episode.
        </p>
        <h3>Members-Only Live Room Episodes</h3>
        <p>
          Members-only Live Room episodes are exclusive to subscribers. Currently, members cannot
          invite guests, but we are working to introduce this feature soon, along with other
          exciting membership perks.
        </p>
      </div>
    ),
  },
  {
    key: 'contacting-creators',
    label: 'Contacting Creators',
    topic: LIVE_ROOM,
    Html: (
      <div>
        <p>
          Whether you have a question about a Live Room or a Creator’s project, or want to leave a
          personal message of support, contacting a Creator starts with a simple message. Your
          message threads are stored in your inbox, where you can manage all your communication and
          activity logs.
        </p>
        <h3>Ways to Send Messages to Creators:</h3>
        <ul>
          <li>Go to your Inbox and select an existing thread or start a new one.</li>
          <li>Visit a Live Room and click Contact Creator.</li>
          <li>
            For membership or support questions, click Subscribe or Donation and then Contact
            Creator.
          </li>
        </ul>
        <h3>Leave a Personal Note:</h3>
        <ul>
          <li>
            When booking a Live Room episode or subscribing to a membership, you can include a
            personal message to the Creator. This helps build a direct connection, and we encourage
            leaving a thoughtful note to motivate the Creator.
          </li>
        </ul>
      </div>
    ),
  },
  {
    key: 'managing-live-room-episodes-in-calendar',
    label: 'Managing Live Room Episodes in Calendar',
    topic: LIVE_ROOM,
    Html: (
      <div>
        <p>
          Stay up-to-date with all your Live Room episodes. Since these are live online events, your
          responsible participation motivates the Creators you support.
        </p>
        <h3>How to Manage Your Live Room Episodes:</h3>
        <ul>
          <li>All booked episodes are available in your Calendar.</li>
          <li>Click each episode in the Calendar to access the event link and details.</li>
          <li>Adjust the time zone in your Calendar to view episodes in your local time.</li>
          <li>
            If you're late or need to cancel, notify the Creator by messaging them through your
            Inbox.
          </li>
        </ul>
      </div>
    ),
  },
  {
    key: 'cancel-or-change-live-room-episodes',
    label: 'Cancel or Change Live Room Episodes',
    topic: LIVE_ROOM,
    Html: (
      <div>
        <p>
          At present, there's no option to cancel a booked Live Room episode. However, we kindly ask
          that you notify the Creator of your absence by sending a personal note. We are
          continuously working to improve the Live Room experience and plan to add more features
          soon.
        </p>
        <p>
          Your feedback is invaluable in helping us grow! Join our Community to share your ideas and
          help shape future updates.
        </p>
      </div>
    ),
  },
  {
    key: 'subscribing-to-live-room-membership',
    label: 'Subscribing to Live Room Membership',
    topic: LIVE_ROOM,
    Html: (
      <div>
        <p>
          Supporting Creators and their productions through membership gives you exclusive access to
          their projects, behind-the-scenes content, and deeper insights into their creative
          process. Here’s how to get started:
        </p>
        <h3>Simple Steps:</h3>
        <ul>
          <li>
            Discover a Production or Creator: Find a production or Creator you’d like to explore
            further.
          </li>
          <li>Subscribe: Click the Subscribe button on their Live Room.</li>
          <li>Review Perks: Check the available membership tiers and perks, then click Manage.</li>
          <li>
            Complete Payment: Enter your credit card details on the check-out page and click Confirm
            Payment.
          </li>
          <li>
            Enjoy Exclusive Access: Unlock members-only content while supporting the Creator’s work!
          </li>
        </ul>
        <h3>Members-Only Live Room Episodes:</h3>
        <p>
          Episodes marked with a star (<Icon name="premium" size={16} className={css.star} />) are
          for members only, accessible to subscribers.
        </p>
        <h3>One-Time Billing:</h3>
        <p>
          Live Room memberships operate on a one-time billing system, valid for a specific duration.
          Be sure to review the membership period before making a purchase.
        </p>
      </div>
    ),
  },
  {
    key: 'how-to-receive-membership-perks',
    label: 'How to Receive Membership Perks',
    topic: LIVE_ROOM,
    Html: (
      <div>
        <p>
          Each Creator sets their own membership perks, which may differ across Live Rooms. All
          memberships include access to members-only episodes. To view the perks, membership levels,
          and duration, click the Manage button. If you have any questions, feel free to reach out
          to the Creator directly.
        </p>
        <h3>Type of Perks</h3>
        <ul>
          <li>Members-only live events (default)</li>
          <li>Promo codes for tickets*</li>
          <li>Merch discounts*</li>
          <li>Shout-outs</li>
          <li>Early ticket access*</li>
          <li>Production credits</li>
          <li>Special event invitations*</li>
        </ul>
        <p>*These perks will be delivered via message directly from the Creator.</p>
        <h3>Access to Members-Only Live Room Episodes:</h3>
        <p>
          Episodes marked with a star (<Icon name="premium" size={16} className={css.star} />) are
          exclusive to members. Simply click RSVP, confirm your booking, and you’re all set to
          attend.
        </p>
        <h3>Types of Members-Only Episodes:</h3>
        <p>
          Members-only episodes can include Q&A sessions, exclusive rehearsals, backstage virtual
          tours, and more, depending on the Creator's creativity. Choose episodes based on their
          title and description to enjoy a variety of unique liveRooms.
        </p>
        <h3>Receiving Other Perks:</h3>
        <p>
          Once you purchase a membership, a message thread will open between you and the Creator.
          Any additional perks—such as promo codes and special offers—will be sent directly via
          messages.
        </p>
      </div>
    ),
  },
  {
    key: 'creator-is-not-delivering-benefits',
    label: 'Creator is Not Delivering Benefits',
    topic: LIVE_ROOM,
    Html: (
      <div>
        <p>
          Attractr does not directly manage membership perk delivery, but here are some steps you
          can take if your Creator isn’t meeting your expectations:
        </p>
        <h3>Send the Creator a Message:</h3>
        <p>
          If your perks are delayed, first reach out directly to the Creator. There might be a valid
          reason for the delay, or it could be a simple oversight or delivery issue.
        </p>
        <h3>Request a Refund:</h3>
        <p>
          If you'd prefer not to wait for the benefits, you can request a refund directly from the
          Creator. Full refunds are available until the 20th of the month of your subscription,
          provided you haven’t accessed any members-only content. To request a refund, please
          contact <a href="mailto:team@attractr.live">team@attractr.live</a> for assistance.
        </p>
        <h3>Cancel Your Membership:</h3>
        <p>
          If the issue remains unresolved, contact{' '}
          <a href="mailto:team@attractr.live">team@attractr.live</a> to assist with canceling your
          membership after attempting to resolve the issue with the Creator.
        </p>
      </div>
    ),
  },
  {
    key: 'how-to-check-your-membership-status',
    label: 'How to Check Your Membership Status',
    topic: LIVE_ROOM,
    Html: (
      <div>
        <p>You can view your membership details on the Subscriptions page. To check your status:</p>
        <ul>
          <li>Go to the menu and click Subscriptions.</li>
          <li>You'll find the following information:</li>
          <ul>
            <li>Live Rooms you’ve subscribed to</li>
            <li>Membership tier</li>
            <li>Current status</li>
            <li>Expiration date</li>
          </ul>
        </ul>
        <p>You’ll also receive a reminder message a few days before your membership expires.</p>
      </div>
    ),
  },
  {
    key: 'what-is-donate-on-live-room',
    label: 'What is "Donate" on Live Room',
    topic: LIVE_ROOM,
    Html: (
      <div>
        <p>
          Attractr allows you to directly support creative projects by donating through the Donate
          feature. Donations are a one-time contribution that helps Creators bring their visions to
          life.
        </p>
        <h3>Who Can Donate:</h3>
        <ul>
          <li>
            Anyone with an Attractr account can make a donation, even without subscribing to the
            Live Room.
          </li>
        </ul>
        <h3>How to Donate:</h3>
        <ul>
          <li>Visit the Live Room you wish to support.</li>
          <li>Click Donate and select your donation amount.</li>
          <li>Click Donate, enter your payment information, and confirm your payment.</li>
        </ul>
        <p>For questions, feel free to contact the Creator before donating.</p>
      </div>
    ),
  },
  {
    key: 'how-to-leave-comments-on-live-room',
    label: 'How to Leave Comments on Live Room',
    topic: LIVE_ROOM,
    Html: (
      <div>
        <p>
          Make your voice heard! After engaging with a Live Room episode, you can leave comments and
          reviews to share your thoughts and connect with the community.
        </p>
        <h3>How to Leave a Comment:</h3>
        <ul>
          <li>
            To leave a comment, you must have an Attractr account and be subscribed to the Live
            Room, either through a free or paid membership. Once subscribed, you’ll gain access to
            the Live Room’s Discourse section, where you can leave comments and interact with other
            members.
          </li>
        </ul>
      </div>
    ),
  },
  {
    key: 'how-to-leave-review-ratings-on-show-pages',
    label: 'How to Leave Review & Ratings on Production Pages',
    topic: SHOW_PAGE,
    Html: (
      <div>
        <h3>What are Attractr ratings?</h3>
        <p>
          Attractr registered users can rate each Production Page in the database on a scale of 1 to
          10. These individual votes are aggregated into a single Attractr rating, which is
          displayed on the main Production Page. You can update your rating as often as you like,
          but each new rating replaces the previous one—so it’s one vote per production per user.
        </p>
        <h3>How Do I See My Ratings?</h3>
        <p>
          You can view your ratings directly on individual Production Pages and your profile page.
        </p>
        <h3>How Do I Leave A Review?</h3>
        <p>To leave a review on the desktop website:</p>
        <ul>
          <li>
            Navigate to the Production Page, scroll down and click + Review at the User reviews
            section.
          </li>
          <li>You'll be presented with a form where you can:</li>
          <ul>
            <li>Update or add your star rating</li>
            <li>Write a detailed review (be sure to note any spoilers).</li>
          </ul>
          <li>Once you're finished, click Publish Your Review.</li>
        </ul>
        <p>
          <strong>Note:</strong> The minimum length for reviews is 600 characters. Reviews filled
          with junk text will be discarded to maintain quality.
        </p>
        <h3>How Do I See My Review?</h3>
        <p>Your Ratings are available on the Production Page and your Profile Page.</p>
        <p>
          We’re developing a new feature that will allow audiences to effortlessly collect and track
          what they've watched, reviewed, or added to their watchlist, all in one convenient place.
          Stay tuned!
        </p>
      </div>
    ),
  },
  {
    key: 'can-i-buy-a-ticket-from-show-page',
    label: 'Can I Buy A Ticket From Production Page?',
    topic: SHOW_PAGE,
    Html: (
      <div>
        <p>
          No, Attractr does not sell tickets directly through our platform. However, you can easily
          find tickets by clicking the "Get Tickets" button, which will take you to the relevant
          ticketing page for your chosen event.
        </p>
      </div>
    ),
  },
  {
    key: 'can-i-bookmark-a-show',
    label: 'Can I Bookmark A Production?',
    topic: SHOW_PAGE,
    Html: (
      <div>
        <p>
          Yes! You can easily bookmark Production Pages by clicking the + ribbon icon on the
          Production Page. This saves the production to your Watchlist, allowing you to track
          upcoming events and productions you’re interested in. Access My Watchlist from the landing
          page to ensure you never miss out on what’s coming next!
        </p>
      </div>
    ),
  },
  {
    key: 'paying-for-membership',
    label: 'Paying for Membership',
    topic: PAYMENTS_AND_REFUNDS,
    Html: (
      <div>
        <p>
          Supporting Creators through membership helps bring their projects to life. After selecting
          a membership package, follow these steps to complete your payment:
        </p>
        <h3>Payment Method:</h3>
        <ul>
          <li>
            We accept credit cards (Visa, MasterCard, Amex, JCB, and debit cards processed as
            credit).
          </li>
          <li>Enter your card details and click Confirm Payment.</li>
          <li>
            To save your card for future transactions, check the Save card details box, or save it
            later under Account `{'>'}` Payment Methods.
          </li>
        </ul>
        <h3>One-Time Payment:</h3>
        <ul>
          <li>
            Memberships are charged as a one-time payment and are valid until the specified end
            date.
          </li>
          <li>Payment is processed immediately upon confirmation.</li>
        </ul>
        <h3>Multiple Memberships:</h3>
        <ul>
          <li>
            You can subscribe to multiple memberships without additional or recurring charges for
            the duration of each membership.
          </li>
        </ul>
        <h3>Messaging Creators:</h3>
        <ul>
          <li>
            If you have questions or need assistance, you can contact Creators directly through your
            Inbox by selecting the relevant message thread from their Live Room.
          </li>
        </ul>
      </div>
    ),
  },
  {
    key: 'your-donation-supports-creators',
    label: 'Your Donation Supports Creators',
    topic: PAYMENTS_AND_REFUNDS,
    Html: (
      <div>
        <p>
          Your donation helps Creators continue and evolve their projects. Available donation
          amounts are $25, $50, $100, $200, or $500.
        </p>
        <h3>Payment Method:</h3>
        <ul>
          <li>
            We accept credit cards (Visa, MasterCard, Amex, JCB, and debit cards processed as
            credit).
          </li>
          <li>Enter your card details and click Confirm Payment.</li>
          <li>
            To save your card for future use, check the Save card details box or add it later under
            Account > Payment Methods.
          </li>
          <li>Your payment is processed and charged immediately upon confirmation.</li>
        </ul>
        <h3>Donor Perks:</h3>
        <p>
          If the Creator offers donor perks (such as shout-outs, private sessions, or early access),
          they will be delivered via message.
        </p>
        <h3>Messaging Creators:</h3>
        <p>
          You can contact Creators directly through your Inbox by selecting the relevant Live Room
          thread.
        </p>
      </div>
    ),
  },
  {
    key: 'cancel-and-refund',
    label: 'Cancel and Refund',
    topic: PAYMENTS_AND_REFUNDS,
    Html: (
      <div>
        <p>
          Attractr is a platform that facilitates payments from members to Creators. Once a payment
          is processed, it is sent to the Creator’s balance. Therefore, if you're canceling due to
          undelivered perks, we encourage you to first reach out to the Creator to resolve the
          issue.
        </p>
        <h3>Cancel a Scheduled Live Room Episode:</h3>
        <p>
          If something unexpected arises and you need to cancel a scheduled Live Room episode, it’s
          recommended to message the Creator and notify them of your absence. You may also want to
          inquire about rescheduling to join a future episode. Communication is key to maintaining a
          good relationship with the Creator and keeping your experience on track.
        </p>
        <h3>Request a Refund from the Creator:</h3>
        <p>
          If you're not receiving what was promised from a Creator, we highly recommend contacting
          the Creator directly to request the perks. If after this, you still do not receive the
          expected benefits, you can request a cancellation and refund. Refunds are available within
          48 hours of your subscription, provided you haven’t accessed any perks or members-only
          content. If you waited long enough but still haven’t received what was promised, you can
          contact <a href="mailto:team@attractr.live">team@attractr.live</a> to request a refund.
          Keep in mind that refunds are granted at our discretion, and you must reach out by the
          20th of the month in which you purchased the membership. If the refund is approved, it
          will be processed by deducting from the Creator’s balance.
        </p>
        <h3>How Refunds Work at Attractr:</h3>
        <p>
          Attractr does not process refunds on behalf of Creators, but we do assist with issues and
          help facilitate refunds by deducting the refunded amount from the Creator’s balance.
          Creators receive payouts on the 20th of each month, which is why any refund requests must
          be made before the 20th of the same month in which you subscribed. If you fail to request
          a refund by then, we may not be able to process your request, as funds are transferred to
          the Creator.
        </p>
        <h3>Can I Request a Refund for a Donation?</h3>
        <p>
          No, donations are non-refundable. When you donate to a Creator through the Donate feature,
          it is considered a one-time contribution to support their creative efforts, and refunds
          are not applicable.
        </p>
      </div>
    ),
  },
  {
    key: 'receipts',
    label: 'Receipts',
    topic: PAYMENTS_AND_REFUNDS,
    Html: (
      <div>
        <p>
          Need receipts for record-keeping? You can easily download and print them directly from
          your Attractr account.
        </p>
        <h3>To Access Your Billing History:</h3>
        <ul>
          <li>Log in to your Attractr account and click Order History from the top menu.</li>
          <li>
            Your billing history will display the processing date, the amount charged, and the type
            of purchase (membership or donation).
          </li>
          <li>You will also receive a message from Attractr with a receipt link attached.</li>
        </ul>
        <h3>To Download Your Receipts:</h3>
        <ol>
          <li>Go to Order History.</li>
          <li>Click the transaction that you want to download a receipt.</li>
        </ol>
        <p>
          <strong>Note:</strong> Currency conversions may appear on your bank statement if your
          local currency differs from the Creator's billing currency.
        </p>
      </div>
    ),
  },
  {
    key: 'taxes',
    label: 'Taxes',
    topic: PAYMENTS_AND_REFUNDS,
    Html: (
      <div>
        <p>
          Since Attractr operates from Canada on a global scale, tax obligations are based on the
          buyer’s location (audience members). Here’s a breakdown of the taxes Attractr may collect:
        </p>
        <h3>Canadian Sales Tax (GST/HST/QST/PST)</h3>
        <ul>
          <li>
            Canadian Buyers*: Attractr collects applicable GST/HST, QST, or PST based on the buyer’s
            province. For example, Ontario buyers pay HST at Ontario’s rate, while Quebec buyers pay
            GST and QST.{' '}
            <i>(*Tax will be calculated based on the IP address location of the transaction.)</i>
          </li>
          <li>
            International Creators: Canadian sales tax applies to Canadian buyers, regardless of the
            supplier’s location.
          </li>
        </ul>
        <h3>Tax Rate</h3>
        <ul>
          <li>GST: 5% (applies nationwide).</li>
          <li>HST: Varies by province (e.g., 13% in Ontario, 15% in Nova Scotia).</li>
          <li>QST: 9.975% (applies in Quebec)</li>
        </ul>
        <p>
          <strong>Note:</strong> Total applicable taxes are displayed at checkout.
        </p>
      </div>
    ),
  },
  {
    key: 'creating-a-show-page',
    label: 'Creating a Production Page',
    topic: BECOMING_A_CREATOR,
    Html: (
      <div>
        <p>
          Welcome to Attractr! Follow these steps to set up your Production Page, a dynamic hub that
          centralizes your marketing efforts, collaborates with cast and crew, connects with
          audiences, and drives ticket sales.
        </p>
        <h3>What is a Production Page?</h3>
        <p>
          A Production Page is more than just an event listing—it’s an interactive platform where
          you can showcase your team, including artists, technicians, and more. Through Live Rooms,
          you can share exclusive behind-the-scenes content, making-of footage, and gather valuable
          audience feedback. By creating an engaging space, you invite audiences to explore and
          connect emotionally with your project. When audiences relate to your creators, they’re
          more likely to support your production and attend in person. A Production Page is
          essential for streamlining your marketing efforts and fostering community engagement.
        </p>
        <p>
          In addition, both audiences and creators can leave reviews and ratings on Production
          Pages, building a valuable repository of knowledge and feedback throughout the
          production’s lifespan. Once a Production Page is created, there’s no need to build a new
          one when touring or performing at different venues—it grows and evolves over time.
        </p>
        <h3>Before Getting Started:</h3>
        <p>To ensure a smooth setup, gather the following before creating your Production Page:</p>
        <ul>
          <li>Poster Image 1080 x 1920 pixels (9:16 ratio)</li>
          <li>Short clips (1080 x 1920 pixels (9:16 ratio), up to 30 seconds, 5 MB)</li>
          <li>
            Live Room ideas: Collaborators, behind-the-scenes content, and perks (promo codes,
            discounts, special invites).
          </li>
        </ul>
        <h3>Create an Organization Profile</h3>
        <p>Set up your Organization Profile:</p>
        <ul>
          <li>
            After signing up as a Creator, you'll access the Creator Dashboard. If you're creating a
            Production Page for a production company, go to Settings > Organization Profiles and
            click +Add Organization Profile or edit "New project” to create your company’s profile.
          </li>
          <li>
            If your Organization Profile already exists, ask your team to add you as a Team Admin so
            you can manage and create pages for your production company.
          </li>
        </ul>
        <h3>Production at Any Stage:</h3>
        <p>
          No matter what stage your production is in—whether in development or actively on tour—you
          can submit it on Attractr. Choose the current status of your production:
        </p>
        <ul>
          <li>On tour</li>
          <li>In theater</li>
          <li>Presales</li>
          <li>In development</li>
          <li>Looking for funds</li>
        </ul>
        <h3>Create Your Production Page:</h3>
        <p>
          Creating a Production Page is simple! Click +Create Pages and select Create a Production
          Page.
        </p>
        <ul>
          <li>
            Details: Enter the required information about your production and select your
            Organization Profile (Production Company). If you haven’t created an Organization
            Profile yet, select +Create an Organization Profile.
          </li>
          <li>
            Content: Showcase your production with photos and videos. To set a thumbnail, click the
            three dots on the uploaded picture and choose Set Poster. Videos should be short clips
            (up to 30 seconds, 5 MB) to offer behind the scenes glimpses of your production. You can
            add new clips at any time.
          </li>
          <li>
            Cast & Crew: Invite your artists and crew members who contributed to the production by
            clicking Send an Invite. Add their email address and provide credit (title and year). To
            make them a Team, check the box labeled Add as a Team to your organization.
          </li>
          <li>
            Touring & Links: Add your external ticketing link and tour dates. If not yet on sale,
            select the appropriate availability status.
          </li>
        </ul>
        <p>
          Don’t worry! You can always update and refine your Production Page after it’s published.
        </p>
        <h3>Review and Approval:</h3>
        <p>
          Attractr reviews each Production Page to ensure it meets quality standards, contributing
          to a vibrant and engaging community. Once the review is complete, you’ll receive a
          notification, and your Production Page will be published. Please note that your Production
          Page will not go live until you create at least one Live Room linked to it.
        </p>
        <h3>Create multiple Production Pages</h3>
        <p>
          You can create multiple Production Pages, from past to future productions, and build a
          beautiful community around your productions.
        </p>
      </div>
    ),
  },
  {
    key: 'creating-a-live-room',
    label: 'Creating a Live Room',
    topic: BECOMING_A_CREATOR,
    Html: (
      <div>
        <p>
          Behind-the-scenes and making-of content offers your audience an exclusive look into your
          creative process, fostering a deeper emotional connection with your project. It’s more
          than just a glimpse—it’s an invitation to join you on your journey. These
          behind-the-scenes moments not only offer opportunities for monetization but also foster
          communication, sharing, and engagement with your audience.
        </p>
        <h3>Who Can Create a Live Room?</h3>
        <p>
          A Live Room is a membership-based Creator page that allows you and your team to
          collectively promote your production through personal perspectives. The following can
          create a Live Room:
        </p>
        <ul>
          <li>Creators with a Production Page.</li>
          <li>Creators who are added as a Team to the Organization.</li>
        </ul>
        <h3>Before Getting Started:</h3>
        <p>We recommend gathering the following before setting up your Live Room on Attractr:</p>
        <ul>
          <li>Cover photo (1024 x 768 pixel image with a 4:3 aspect ratio).</li>
          <li>Short clips (up to 30 seconds, 5 MB each) to tease or highlight your project.</li>
        </ul>
        <p>
          Behind-the-scenes content can include anything that gives your audience an authentic and
          untold glimpse into your project. It’s like a live documentary, capturing how your project
          comes to life through each Creator’s unique lens. Creators can include not just
          performers, but also technicians, riggers, costume designers, musicians, stage managers,
          and more.
        </p>
        <h3>Here are some examples of behind-the-scenes content that audiences love:</h3>
        <ul>
          <li>Daily training at the studio or home.</li>
          <li>Off-day activities.</li>
          <li>Street performances or flash mobs.</li>
          <li>Rehearsals or working sessions.</li>
          <li>Creative brainstorming or concept workshops.</li>
          <li>Stretching and warm-up routines for show days.</li>
          <li>Backstage tours.</li>
          <li>Stage building or scenography sessions.</li>
          <li>Post-show chats in the green room or at a restaurant.</li>
          <li>Wardrobe and beauty tips.</li>
          <li>Exploring new touring cities.</li>
          <li>The hour before showtime.</li>
          <li>The sky's the limit!</li>
        </ul>
        <h3>Plan Your Live Room Episodes:</h3>
        <p>
          A Live Room is an online video experience that lets you engage with your audience through
          tiered memberships. All you need is a mobile device or desktop to go live and share
          behind-the-scenes moments in real time. Here’s what to consider before starting your Live
          Room:
        </p>
        <ul>
          <li>What is your Live Room about?</li>
          <li>
            Name your Live Room (we recommend using your name if you don’t have a branded title).
          </li>
          <li>What perks can you offer for memberships?</li>
          <li>How often can you go live to share behind-the-scenes experiences?</li>
        </ul>
        <p>
          Your Live Room serves as your personal channel and can remain active even after the
          associated production concludes. You can link it to new productions you’re involved in,
          maintaining continuity with your audience. However, if you are removed as a Team member of
          the organization, you will lose ownership of the Live Room. We are actively working on
          enhancing Team permission features to ensure ownership continuity for Creators. Stay tuned
          for updates!
        </p>
        <h3>Create a Membership:</h3>
        <p>
          Once you’ve added the details and branding for your Live Room, it’s time to set up your
          membership tiers. You can create as many tiers as you like, but ensure that you can
          deliver the promised perks to your audience.
        </p>
        <ul>
          <li>
            Pick a membership tier title (e.g., Member, Lite, Elite, Pro, Teaser, Early Birds, Super
            Fan, etc.).
          </li>
          <li>Add a tier description.</li>
          <li>Set a price and membership duration.</li>
          <li>Select perks for each tier.</li>
        </ul>
        <h3>Types of Perks:</h3>
        <ul>
          <li>Members-only live episodes (default).</li>
          <li>Promo codes for tickets (delivered by message).</li>
          <li>Discounts on merch (delivered by message).</li>
          <li>Shout-outs.</li>
          <li>Early ticket access (delivered by message).</li>
          <li>Credit as part of the production when launched.</li>
          <li>Special event invitations (delivered by message).</li>
        </ul>
        <p>
          <strong>Note:</strong> Members-only live episodes are the default perk for all Live Room
          memberships. Every subscriber will receive this perk, so you’ll need to schedule
          members-only Live Room timeslots when creating memberships.
        </p>
        <h3>Membership Payment and Duration:</h3>
        <p>
          Memberships are one-time payments, with durations set by you. The membership is valid for
          the specified number of days or months from the purchase date. This allows you to create
          memberships tailored to different audience journeys, such as:
        </p>
        <ul>
          <li>One-month memberships for users who aren’t sure about buying tickets yet.</li>
          <li>
            Six-month memberships for super fans who want to support and engage throughout your
            project.
          </li>
        </ul>
        <h3>Add Live Room Event Timeslots:</h3>
        <p>
          Once your membership tiers are set, you can begin scheduling Live Room Episodes—both free
          and paid—for at least the coming month.
        </p>
        <ul>
          <li>Click +Add timeslot.</li>
          <li>
            Enter the title and select the type of Live Room event (e.g., Early Access, BTS, Q&A,
            Coffee Talk).
          </li>
          <li>Set the date and time (choose your time zone).</li>
          <li>To repeat the episode, add more dates.</li>
          <li>Choose the access type: Everyone (free) or Members-only (paid subscribers).</li>
        </ul>
        <h3>Preview and Save:</h3>
        <p>
          Once your Live Room is set up, click Preview to see how it looks. When everything is
          ready, click Save to publish.
        </p>
        <h3>Can I Create Multiple Live Rooms?</h3>
        <p>Yes, creators can create and manage multiple Live Rooms.</p>
      </div>
    ),
  },
  {
    key: 'editing-and-adding-memberships',
    label: 'Editing and Adding Memberships',
    topic: BECOMING_A_CREATOR,
    Html: (
      <div>
        <p>After launching a Live Room, you can easily add, edit, or cancel your memberships.</p>
        <h3>How to Add or Edit Memberships:</h3>
        <ul>
          <li>Go to the Page Manager and select the Live Room you want to edit.</li>
          <li>Click Edit Listing.</li>
          <li>Select Membership.</li>
          <li>
            To edit an existing membership, click the edit icon, make your changes, and click Save.
          </li>
          <li>To add a new membership, click Add Membership and fill in the required details.</li>
        </ul>
        <h3>How to Cancel a Membership:</h3>
        <ul>
          <li>Go to the Page Manager and select the Live Room you want to edit.</li>
          <li>Click Edit Listing.</li>
          <li>Select Membership.</li>
          <li>Choose the membership you want to cancel and click the delete icon.</li>
        </ul>
        <h3>Who Will Be Affected by the Change:</h3>
        <p>
          Changes will only apply to new subscribers. If you edit or cancel a membership, you are
          still responsible for delivering the promised perks to existing subscribers for the
          remainder of their membership duration. Be sure to track your interactions and deliveries
          to existing members through Inbox and Insights > Members, where you can check their Tier
          type and End date.
        </p>
      </div>
    ),
  },
  {
    key: 'delivering-perks-to-your-members',
    label: 'Delivering Perks to Your Members',
    topic: BECOMING_A_CREATOR,
    Html: (
      <div>
        <p>
          Live Rooms offer the opportunity to create members-only perks. These perks are exclusively
          available to your active, paid members—your super fans who are there to support you. As a
          Creator, it's your responsibility to deliver these perks and keep your members engaged.
        </p>
        <h3>Program Members-Only Live Room Episodes Regularly:</h3>
        <p>
          You can schedule members-only Live Room episodes that are accessible only to your active
          members. While anyone can discover the episode, only members can attend live. To maintain
          engagement, be sure to schedule these episodes frequently or periodically.
        </p>
        <ul>
          <li>Go to Calendar.</li>
          <li>Click Add timeslot and select a Live Room.</li>
          <li>Set the timeslot for Members only.</li>
          <li>
            If the event is for a specific paid member, click Invite and type the member’s name.
          </li>
        </ul>
        <h3>Deliver Perks via Message:</h3>
        <p>
          For perks such as promo codes, merch discounts, early ticket access, or special event
          invitations (online or offline), you can deliver them directly to your members via
          messages.
        </p>
        <ul>
          <li>Go to Audience > Members.</li>
          <li>Select a paid member and click Message.</li>
          <li>Send the perk information directly to the member.</li>
        </ul>
        <h3>Use Quick Replies:</h3>
        <p>
          To streamline the process, you can use Quick Replies to create message templates for
          delivering personalized perks or messages to your members.
        </p>
        <ul>
          <li>Go to Inbox.</li>
          <li>Click +New Reply.</li>
          <li>Add a message title (e.g., "Thank you for subscribing to my Live Room!").</li>
          <li>Create a message template and click Submit.</li>
          <li>
            In each message thread, click Quick Reply and select the template you want to use.
          </li>
        </ul>
      </div>
    ),
  },
  {
    key: 'managing-your-live-room-episodes',
    label: 'Managing Your Live Room Episodes',
    topic: BECOMING_A_CREATOR,
    Html: (
      <div>
        <p>
          Since Live Room is an online live video chat experience, all Live Room episodes—whether
          free or paid—are scheduled to go live at specific times. While building an audience may
          take time, posting regularly is one of the best ways to grow your community and increase
          your income. Regular posting could mean daily, weekly, or monthly, depending on what works
          best for you, your content, and your subscribers. Using a content calendar aligned with
          your project schedule will help you plan and organize your Live Room episodes effectively.
        </p>
        <h3>How to Schedule Live Room Events:</h3>
        <ul>
          <li>Go to Calendar.</li>
          <li>Click Add timeslot and select your Live Room.</li>
          <li>Click +Add timeslot.</li>
          <li>
            Fill in the title and choose the type of event (e.g., Early Access, BTS, Q&A, Coffee
            Talk, etc.).
          </li>
          <li>Set the date, time, and duration.</li>
          <li>To repeat the episode, add additional dates.</li>
          <li>Choose the access type: Everyone (free) or Members-only (paid subscribers).</li>
          <li>Click Add.</li>
        </ul>
        <h3>How to Cancel a Live Room Event:</h3>
        <ul>
          <li>Go to Page Manager and select the Live Room.</li>
          <li>Click Edit listing.</li>
          <li>Go to Schedules.</li>
          <li>Click the delete icon next to the timeslot you need to cancel.</li>
          <li>Members who have already booked the event will receive a notification.</li>
        </ul>
        <h3>How to Edit Your Live Room Timeslots:</h3>
        <ul>
          <li>Go to Calendar.</li>
          <li>Select the Live Room event you want to edit.</li>
          <li>Make any necessary changes (e.g., title, date, time, or access type).</li>
          <li>Click Save to update.</li>
        </ul>
      </div>
    ),
  },
  {
    key: 'how-to-start-a-live-room-episode',
    label: 'How to Start a Live Room Episode',
    topic: BECOMING_A_CREATOR,
    Html: (
      <div>
        <p>
          When you create a Live Room event by adding timeslots, each event automatically generates
          a video link, which is shared with audiences who book the event.
        </p>
        <h3>About Live Room Events</h3>
        <p>
          Attractr uses Whereby, a browser-based video meeting tool that offers high-quality,
          secure, and easy-to-use video conferencing. Each Live Room event can currently host up to
          100 participants.
        </p>
        <h3>What You Need Before Starting:</h3>
        <p>
          Unlike platforms like YouTube or Twitch, which require studio setups, high-end cameras,
          and complex audio/visual equipment, Attractr focuses on community building through simple
          online live video liveRooms. To start, all you need is:
        </p>
        <ul>
          <li>A reliable Wi-Fi connection</li>
          <li>A mobile/tablet device or desktop that has built-in camera</li>
        </ul>
        <p>
          Of course, optimizing your setup for better video quality is helpful as your community
          grows, but it's not necessary when you're starting out.
        </p>
        <h3>Changing Video or Audio Input Source (Desktop Only):</h3>
        <ul>
          <li>If you prefer to change your video or audio input on a desktop:</li>
          <li>Go to Settings.</li>
          <li>Navigate to Devices > Camera or Audio.</li>
          <li>From the drop-down list, select your preferred device.</li>
          <li>Click Save.</li>
        </ul>
        <h3>Start Your Live Room Event:</h3>
        <ul>
          <li>Go to Calendar and select the timeslot for your upcoming event.</li>
          <li>Find the video link there.</li>
          <li>Click the link to start your Live Room event and welcome your audience.</li>
        </ul>
        <h3>What You Can Do in a Live Room:</h3>
        <ul>
          <li>Live chat with participants</li>
          <li>Live stream to YouTube/Vimeo</li>
          <li>Screen share to show your audience additional content</li>
        </ul>
      </div>
    ),
  },
  {
    key: 'messaging-members',
    label: 'Messaging Members',
    topic: BECOMING_A_CREATOR,
    Html: (
      <div>
        <p>
          Personalized messages from creators are a huge perk for audiences and members. When
          someone books or subscribes to your Live Room, a message thread is automatically generated
          between you and the audience member. You can then send direct messages to them.
        </p>
        <h3>How to Send a Message to Your Member:</h3>
        <ul>
          <li>Go to Inbox.</li>
          <li>Select the person you want to message and start the conversation.</li>
        </ul>
        <h3>What is the Green Dot?</h3>
        <p>
          The green dot indicates when you or your audience members are available online. You can
          see which of your connections are available to receive real-time messages by looking for
          the green dot next to their profile picture in the inbox.
        </p>
        <h3>Use Quick Replies:</h3>
        <p>
          To save time, you can use Quick Replies to send personalized messages or deliver perks to
          paid members.
        </p>
        <ul>
          <li>Go to Inbox.</li>
          <li>Click +New reply.</li>
          <li>Add a message title (e.g., "Thank you for subscribing to my Live Room!").</li>
          <li>Create a message template and click Submit.</li>
          <li>In any message thread, click Quick Reply and choose the template you want to use.</li>
        </ul>
        <h3>How to Create a Group Chat for Live Room Episode Participants:</h3>
        <p>
          You can easily create a group chat for participants in a specific Live Room event,
          allowing them to engage with each other and with you.
        </p>
      </div>
    ),
  },
  {
    key: 'kickstarting-a-community-on-attractr',
    label: 'Kickstarting A Community on Attractr',
    topic: GROW_YOUR_COMMUNITY,
    Html: (
      <div>
        <p>
          Attractr is a powerful tool for production companies aiming to build an engaged audience
          around their productions by working collaboratively with artists and creators. This guide
          provides essential steps to help you build a thriving community that supports your current
          productions and fosters long-term engagement.
        </p>
        <h3>Step 1: Create Your Organization Profile</h3>
        <p>
          Start by setting up an Organization Profile that clearly represents your production
          company’s identity. This includes:
        </p>
        <ul>
          <li>Logo: A recognizable logo that reinforces your brand.</li>
          <li>Bio: A compelling bio that tells your story.</li>
          <li>
            Organization details: Provide essential information that adds credibility to your
            profile.
          </li>
        </ul>
        <p>
          Your Organization Profile establishes the first impression for your community and creates
          a professional foundation for further growth.
        </p>
        <h3>Step 2: Create Production Pages</h3>
        <p>
          Next, build out your production catalog by creating Production Pages for each of your
          projects:
        </p>
        <ul>
          <li>
            Complete the details: Provide all necessary information about the production to give
            audiences a comprehensive view.
          </li>
          <li>
            Invite Cast & Crew: Encourage your team to join, adding legitimacy to the production and
            boosting collaborative promotion.
          </li>
          <li>
            Visual Branding: Add a poster thumbnail and behind-the-scenes videos to showcase the
            production visually and make the page more enticing for visitors.
          </li>
        </ul>
        <p>
          Each Production Page acts as the hub for your production, allowing you to promote the
          project, build an audience, and connect with cast and crew.
        </p>
        <h3>Step 3: Create Live Rooms</h3>
        <p>
          Use Live Rooms to create meaningful interactions with your audience. Begin by establishing
          a production-company-owned Live Room and invite cast and crew as a Team to your
          Organization to host their own Live Rooms under the associated Production Page:
        </p>
        <ul>
          <li>
            Be Authentic: Regularly schedule Live Room episodes that showcase the behind-the-scenes
            journey, from rehearsals to daily activities. Episodes can range from 30 minutes to 5
            hours, depending on the content.
          </li>
          <li>
            Engage with Your Audience: Sharing real, unscripted moments helps build a deeper
            connection with your audience and keeps them invested in your production. Live Rooms
            provide the perfect space for real-time interaction.
          </li>
        </ul>
        <h3>Step 4: Define Unique Value and Perks</h3>
        <p>To truly engage your audience, offer unique perks that align with their interests:</p>
        <ul>
          <li>
            Exclusive content: Behind-the-scenes footage, early access to content, or exclusive
            behind the scenes glimpses.
          </li>
          <li>Personalized liveRooms: Shout-outs, Q&A sessions, or virtual tours.</li>
        </ul>
        <p>
          Tailor perks based on what resonates most with your audience, and offer them through
          membership tiers or Live Room episodes to incentivize participation.
        </p>
        <h3>Step 5: Find Your First Audience</h3>
        <p>Kickstart your community by reaching out to your initial supporters:</p>
        <ul>
          <li>
            Email your newsletter subscribers: Invite them to join Attractr and support your
            projects.
          </li>
          <li>
            Promote on social media: Share updates, Live Room links, and behind-the-scenes content
            to attract your existing followers.
          </li>
          <li>
            Network on Attractr: Join other creators’ communities, support their work, and invite
            them to your own Live Rooms.
          </li>
          <li>
            Join the Attractr Community Forum: Engage with others, share ideas, and collaborate with
            fellow creators to expand your reach.
          </li>
        </ul>
        <p>
          Your early audience is vital for word-of-mouth growth, so cultivate meaningful
          relationships and encourage them to spread the word.
        </p>
        <h3>Step 6: Keep Growing</h3>
        <p>
          Consistency is key to sustaining your community’s growth. Continue hosting regular Live
          Room episodes, share exclusive content, and interact with your members. As your community
          grows, so will your ticket sales, influence, and fan loyalty. Building a passionate
          community takes time, but with ongoing engagement and dedication, your audience will
          expand and thrive.
        </p>
      </div>
    ),
  },
  {
    key: 'how-to-grow-your-members',
    label: 'How To Grow Your Members',
    topic: GROW_YOUR_COMMUNITY,
    Html: (
      <div>
        <p>
          Attractr is designed to help production companies unlock the potential of untold
          behind-the-scenes stories, fueling the growth of their productions at any stage. By
          collaborating with artists and creators, production companies can streamline marketing
          efforts while engaging audiences in new and exciting ways.
        </p>
        <h3>Collaborative Promotion with Cast & Crew</h3>
        <p>
          By assigning your Cast & Crew as a Team member, they can associate their Live Room to a
          production they are involved in and collectively promote it. Any Live Rooms associated
          with a Production Page will be collected under that page, giving Organization Admins
          access to all members from those Live Rooms via Insights > Members. This member database
          provides valuable opportunities to engage fans, build loyalty, and drive traffic to
          theaters while gaining insights into your audience’s preferences.
        </p>
        <h3>How to Grow Your Members:</h3>
        <p>
          There’s no single method for growing your membership base, but here are some best
          practices for leveraging Live Rooms and behind-the-scenes content:
        </p>
        <ul>
          <li>
            Complete Your Cast & Crew: Make sure to assign credits to all contributors on your
            Production Page, enhancing visibility for everyone involved.
          </li>
          <li>
            Empower Your Cast & Crew: Enable your cast and crew to host Live Rooms during
            production, such as backstage tours, rehearsals, or other exclusive behind-the-scenes
            moments.
          </li>
          <li>
            Offer Perks: Provide incentives for creators to design memberships that encourage ticket
            sales and audience engagement.
          </li>
          <li>
            Add Team Admins: Collaborate by adding team admins who can help manage creators and Live
            Rooms, ensuring smooth execution and more member interaction.
          </li>
        </ul>
        <h3>Turn Behind-The-Scenes into a Media Business:</h3>
        <p>
          Allowing your cast and crew to host Live Rooms means giving them access to capture
          behind-the-scenes moments that bring the production to life for your audience. Since
          Attractr currently supports live-format content only, it’s essential to encourage
          audiences to RSVP for specific time slots so they can join these exclusive liveRooms. By
          transforming behind-the-scenes content into engaging media, you create a new avenue for
          revenue and fan engagement.
        </p>
      </div>
    ),
  },
  {
    key: 'turning-your-behind-the-scenes-to-media-business',
    label: 'Turning Your Behind-The-Scenes to Media Business',
    topic: GROW_YOUR_COMMUNITY,
    Html: (
      <div>
        <p>
          Behind-the-scenes content offers production companies a powerful way to transform everyday
          moments into engaging, profitable media liveRooms. By showcasing the raw, real process
          behind your productions, you can craft compelling narratives that captivate your audience
          and create new revenue streams. Here’s how to turn behind-the-scenes content into a
          sustainable media business:
        </p>
        <h3>Step 1: Develop a Strategy</h3>
        <ul>
          <li>
            Set a goal: Define clear objectives for your behind-the-scenes content, whether it’s
            growing your audience, boosting engagement, or driving ticket sales.
          </li>
          <li>
            Identify untold stories: Tap into unused assets like backstage moments, rehearsals, or
            daily routines that highlight the essence of your production.
          </li>
          <li>
            Create a system: Assign team admins to integrate Attractr into your daily workflow,
            enabling artists and creators to host Live Room episodes and managing the delivery of
            perks.
          </li>
          <li>
            Define a Live Room calendar: Establish a consistent schedule for your content to keep
            your audience engaged and anticipate your next update.
          </li>
        </ul>
        <h3>Step 2: Empower Your Artists and Crew</h3>
        <p>
          Your cast and crew have unique talents and personalities that audiences often never get to
          see. By giving them the platform to host their own Live Room episodes, you’re empowering
          them to grow their personal brand and create new revenue streams. Spotlighting their
          untold stories not only enhances the production’s depth but also allows them to engage
          with fans in ways they hadn’t before, adding purpose and value to their passion.
        </p>
        <h3>Step 3: Set the Stage for Payouts</h3>
        <ul>
          <li>
            Add payout accounts: Set up payout accounts for each Live Room, ensuring that membership
            revenues go directly to the Live Room host.
          </li>
          <li>
            Price memberships strategically: Base membership prices on the unique value you offer
            through behind-the-scenes content and perks, such as early access, exclusive insights,
            or personalized shout-outs.
          </li>
        </ul>
        <h3>Step 4: Start Small with Your Closest Networks</h3>
        <ul>
          <li>
            Test your Live Rooms: Start with your initial audience, refining your approach based on
            their feedback.
          </li>
          <li>
            Invite cast and crew: Encourage them to participate in promoting the productions and
            expand their professional networks on Attractr. This also enhances their role as
            ambassadors of your production.
          </li>
        </ul>
        <h3>Step 5: Convert Free Members to Paid</h3>
        <ul>
          <li>
            Host free Live Room episodes: Use free content as a gateway to build interest and
            engagement. This gives audiences a taste of what’s to come and can lead to conversion
            into paid memberships.
          </li>
          <li>
            Send personalized messages: Build relationships through direct communication and
            personalized interactions to nurture free members into loyal, paying subscribers.
          </li>
        </ul>
      </div>
    ),
  },
  {
    key: 'promote-a-show-while-making-money',
    label: 'Promote A Production While Making Money',
    topic: GROW_YOUR_COMMUNITY,
    Html: (
      <div>
        <p>
          Imagine a packed theater with many familiar faces, all of whom supported your journey
          through the Live Room. For producers, marketers, and agents, it's a challenge to attract
          top talent, keep them motivated, and achieve success together. Without creators, there is
          no show. With Attractr, production companies can now promote their productions
          collectively while generating additional income.
        </p>
        <h3>How Attractr Helps You Make Money While Promoting Your Production:</h3>
        <p>
          Attractr gives creators the ability to leverage their talents both on and off the stage.
          By using Live Rooms, creators can turn behind-the-scenes content into engaging stories,
          fostering audience relationships and building a dedicated community. This also opens the
          door to new revenue streams by allowing creators to monetize exclusive content, grow
          memberships, and raise funds.
        </p>
        <h3>1. Offer Free and Paid Content:</h3>
        <p>
          Use free Live Room episodes as an introduction to your community, giving audiences a taste
          of your behind-the-scenes world. Then, encourage them to join your paid memberships for
          access to exclusive content, behind the scenes glimpses, or special live interactions with
          the cast and crew.
        </p>
        <h3>2. Create Tiered Memberships:</h3>
        <p>Offer different membership tiers with varying levels of access. For example:</p>
        <ul>
          <li>
            Basic Tier: Offers access to behind-the-scenes updates and behind the scenes glimpses.
          </li>
          <li>Premium Tier: Includes live interaction, early access to tickets, and shout-outs.</li>
        </ul>
        <p>
          By diversifying membership options, you can cater to a wider range of audience interests
          and increase revenue streams.
        </p>
        <h3>3. Engage and Reward Your Community:</h3>
        <p>
          The more you interact with your audience, the more invested they become. Host regular live
          Q&As, behind-the-scenes tours, or exclusive rehearsals to keep them engaged. Additionally,
          reward loyal members with personalized perks, such as thank-you notes or early ticket
          access.
        </p>
        <h3>4. Leverage Collaboration:</h3>
        <p>
          Invite your cast and crew to participate in Live Rooms and engage with the audience. Their
          unique perspectives and personalities help expand your reach and bring new followers to
          the production, all while collectively promoting your production.
        </p>
        <h3>5. Promote to Your Network:</h3>
        <p>
          Share your Live Room and exclusive content on your social media channels, newsletters, and
          within the Attractr community. Invite current supporters, fans, and followers to join you
          on Attractr and experience the production in a new, immersive way. This creates a snowball
          effect, encouraging more people to participate and potentially invest in your production.
        </p>
        <p>
          With Attractr, your production becomes more than just an onstage experience—it turns into
          a sustainable media business. By combining behind-the-scenes content with audience
          engagement, you can promote your production while generating additional income streams,
          all while strengthening your community.
        </p>
      </div>
    ),
  },
  {
    key: 'promote-a-show-while-making-money',
    label: 'Promote A Production While Making Money',
    topic: GROW_YOUR_COMMUNITY,
    Html: (
      <div>
        <p>
          Imagine a packed theater with many familiar faces, all of whom supported your journey
          through the Live Room. For producers, marketers, and agents, it's a challenge to attract
          top talent, keep them motivated, and achieve success together. Without creators, there is
          no show. With Attractr, production companies can now promote their productions
          collectively while generating additional income.
        </p>
        <h3>How Attractr Helps You Make Money While Promoting Your Production:</h3>
        <p>
          Attractr gives creators the ability to leverage their talents both on and off the stage.
          By using Live Rooms, creators can turn behind-the-scenes content into engaging stories,
          fostering audience relationships and building a dedicated community. This also opens the
          door to new revenue streams by allowing creators to monetize exclusive content, grow
          memberships, and raise funds.
        </p>
        <h3>1. Offer Free and Paid Content:</h3>
        <p>
          Use free Live Room episodes as an introduction to your community, giving audiences a taste
          of your behind-the-scenes world. Then, encourage them to join your paid memberships for
          access to exclusive content, behind the scenes glimpses, or special live interactions with
          the cast and crew.
        </p>
        <h3>2. Create Tiered Memberships:</h3>
        <p>Offer different membership tiers with varying levels of access. For example:</p>
        <ul>
          <li>
            Basic Tier: Offers access to behind-the-scenes updates and behind the scenes glimpses.
          </li>
          <li>Premium Tier: Includes live interaction, early access to tickets, and shout-outs.</li>
        </ul>
        <p>
          By diversifying membership options, you can cater to a wider range of audience interests
          and increase revenue streams.
        </p>
        <h3>3. Engage and Reward Your Community:</h3>
        <p>
          The more you interact with your audience, the more invested they become. Host regular live
          Q&As, behind-the-scenes tours, or exclusive rehearsals to keep them engaged. Additionally,
          reward loyal members with personalized perks, such as thank-you notes or early ticket
          access.
        </p>
        <h3>4. Leverage Collaboration:</h3>
        <p>
          Invite your cast and crew to participate in Live Rooms and engage with the audience. Their
          unique perspectives and personalities help expand your reach and bring new followers to
          the production, all while collectively promoting your production.
        </p>
        <h3>5. Promote to Your Network:</h3>
        <p>
          Share your Live Room and exclusive content on your social media channels, newsletters, and
          within the Attractr community. Invite current supporters, fans, and followers to join you
          on Attractr and experience the production in a new, immersive way. This creates a snowball
          effect, encouraging more people to participate and potentially invest in your production.
        </p>
        <p>
          With Attractr, your production becomes more than just an onstage experience—it turns into
          a sustainable media business. By combining behind-the-scenes content with audience
          engagement, you can promote your production while generating additional income streams,
          all while strengthening your community.
        </p>
      </div>
    ),
  },
  {
    key: 'pages',
    label: 'Pages',
    topic: CREATOR_DASHBOARD,
    Html: (
      <div>
        <p>
          The Creator Dashboard is your central tool to manage and grow your business on Attractr.
          It allows creators to manage their Production Pages and Live Rooms, collectively known as
          Pages. You can create as many Pages as you wish, provided you can manage them effectively.
        </p>
        <h3>Publishing a Production Page:</h3>
        <p>
          To publish a Production Page, click + Create Pages and fill in all required details. Once
          completed, Attractr will review your page to ensure it meets quality standards, keeping
          the community vibrant. During the review process, your Production Page status will appear
          as Under Review in the status column. After approval, you'll receive a notification, and
          your Production Page will be published. Note that your Production Page will not go live
          until you create and link at least one Live Room to it.
        </p>
        <h3>Editing Your Pages After Publication:</h3>
        <p>
          Unlike other platforms, you don’t need to create a new page for every update or city tour.
          Simply update your information as your project evolves, retaining your community along the
          way.
        </p>
        <ul>
          <li>Go to Pages.</li>
          <li>Select the page you want to edit and click Edit Page.</li>
        </ul>
        <h3>Can I Delete My Page?</h3>
        <p>
          You can’t completely delete a Page, but you can close the listing to unlist it from
          Attractr.
        </p>
        <ul>
          <li>Go to Pages.</li>
          <li>Select the listing and click Close Page.</li>
        </ul>
        <h3>Can I Reactivate My Page?</h3>
        <p>Yes, you can easily reopen a closed listing.</p>
        <ul>
          <li>Go to Pages.</li>
          <li>Select the listing and click Open Page.</li>
        </ul>
        <p>Your Page will be published again.</p>
        <h3>Can my Team manage Pages for Creators?</h3>
        <p>
          Yes, your Team member can help manage and support a Creator's Pages. All associated Live
          Rooms and Pages under the Organization Profile are aggregated into the Organization
          Manager’s view (the person who created the Organization profile). The Organization Manager
          can invite Team member, granting them access to manage Pages, Calendar, Inbox, and
          Insights, ensuring smooth coordination and efficient management of the Creator’s content.
        </p>
      </div>
    ),
  },
  {
    key: 'calendar',
    label: 'Calendar',
    topic: CREATOR_DASHBOARD,
    Html: (
      <div>
        <p>
          Scheduling your Live Room episodes is crucial for maintaining audience engagement. We
          recommend listing your regular days and times so members know when to tune in. You can
          easily update your schedule or add specific episodes using the Calendar feature to keep
          everything up to date.
        </p>
        <h3>How to Schedule Live Room Events:</h3>
        <ul>
          <li>Go to Calendar.</li>
          <li>Click Add New Episodes and select your Live Room.</li>
          <li>Click +Add New Episode on Live Room Creation Page</li>
          <li>Complete the event details: title, type, date, time, and duration.</li>
          <li>You can duplicate the episode by adding additional dates.</li>
          <li>Choose access: Free or Members-only.</li>
          <li>Click Add.</li>
        </ul>
        <h3>Live Room Episode Detail</h3>
        <ul>
          <li>
            <strong>Title:</strong> Choose a descriptive and engaging title.
          </li>
          <li>
            <strong>Type:</strong> Select from options like behind the scenes, early access, Q&A,
            workshop, etc.
          </li>
          <li>
            <strong>Timezone:</strong> Set your local timezone.
          </li>
          <li>
            <strong>Date & Time:</strong> Specify the date, start time, and estimated duration.
          </li>
          <li>
            <strong>Repeat:</strong> If the event repeats, add more dates.
          </li>
          <li>
            <strong>Access:</strong> Decide if the event is free or exclusive to members.
          </li>
        </ul>
        <h3>Setting Your Calendar Timezone:</h3>
        <ul>
          <li>Go to Calendar.</li>
          <li>Click Timezone.</li>
          <li>Select the correct timezone for your location.</li>
        </ul>
        <h3>View Upcoming Live Room Events:</h3>
        <ul>
          <li>Click on any upcoming episode to see:</li>
          <ul>
            <li>The schedule (date and time).</li>
            <li>Number of members booked.</li>
            <li>Whether it’s free or members-only.</li>
            <li>The video link for the event.</li>
          </ul>
        </ul>
        <h3>How to Cancel a Live Room Event:</h3>
        <ul>
          <li>Go to Page and choose the Live Room.</li>
          <li>Click Edit Page.</li>
          <li>Under Schedules, click the delete icon to remove the specific episode.</li>
          <li>Members booked for the event will be notified of the cancellation.</li>
        </ul>
        <p>
          <strong>Tip:</strong> If you need to cancel a Live Episode, it’s best to reschedule it and
          send a personalized message to the audience members who booked, inviting them to the new
          schedule. This helps maintain engagement and shows you value their participation.
        </p>
        <h3>How to Edit Your Live Room Episodes:</h3>
        <p>
          At this time, you can’t directly edit episodes once they are created. To make changes, you
          will need to delete the existing episode and schedule a new one.
        </p>
        <h3>Track and Manage The Creator's Episode Schedules</h3>
        <p>
          Team Admins have access to track all Creators' Live Room episodes through a unified
          calendar under the Organization Profile. This centralized view allows admins to
          effectively manage schedules, ensuring smooth coordination. It's helpful to remind
          Creators 5-10 minutes before their episode starts. Additionally, admins can handle
          cancellations, rescheduling, and sending timely messages to audience members, keeping
          communication seamless and episodes on track.
        </p>
      </div>
    ),
  },
  {
    key: 'inbox-and-quick-replies',
    label: 'Inbox and Quick Replies',
    topic: CREATOR_DASHBOARD,
    Html: (
      <div>
        <p>
          Personalized messages are a key perk for audiences and members. When someone books or
          subscribes to your Live Room, an automatic message thread is created, allowing you to
          communicate directly with your audience.
        </p>
        <h3>How to Send a Message to Your Members:</h3>
        <ul>
          <li>Go to Inbox.</li>
          <li>Select the person/thread you want to message.</li>
        </ul>
        <h3>What is the Green Dot?</h3>
        <p>
          The green dot indicates when you're available in real-time. You can also see if your
          members are online by spotting the green dot next to their profile picture.
        </p>
        <h3>How to Create and Send Quick Replies:</h3>
        <p>
          Save time by using Quick Replies for common messages or FAQs. These templates can be
          personalized with shortcodes (e.g., audience name, event details, perks) to make each
          message feel tailored.
        </p>
        <p>
          If you’re co-hosting Live Rooms with a team, all team members with management permissions
          can access and share the same set of Quick Replies. These templates are stored in your
          team’s shared inbox.
        </p>
        <h3>To Create a Quick Reply:</h3>
        <ul>
          <li>Go to your Inbox.</li>
          <li>Click +New Reply.</li>
          <li>Enter a name to identify the template (only visible to your team).</li>
          <li>Type your message and insert shortcodes to personalize it.</li>
          <li>Click Save.</li>
        </ul>
      </div>
    ),
  },
  {
    key: 'organization-settings',
    label: 'Organization Settings',
    topic: CREATOR_DASHBOARD,
    Html: (
      <div>
        <p>
          When you create a Project Page, you need to link it to an Organization/Production Company
          that owns or operates the project.
        </p>
        <p>
          Your Organization Profile serves as a central hub that collects and showcases all of your
          Pages in one view. Every time you publish a new Page, followers of your profile will
          receive email or app notifications from Attractr, helping you grow and connect with
          theater-goers.
        </p>
        <h3>How to Create an Organization Profile:</h3>
        <ul>
          <li>Go to Organization Setting.</li>
          <li>Click +Add Organization Profile.</li>
          <li>Add a profile picture (recommended size: 320x320 pixels).</li>
          <li>
            Fill in other information, such as organization name, website link, and company
            description and details.
          </li>
          <li>Click Save.</li>
          <li>
            If you're in the middle of submitting a Production Page, click Redirect to Your
            Production Page to return and complete your submission.
          </li>
        </ul>
        <h3>How to Add Team members:</h3>
        <ul>
          <li>Go to Organization Settings.</li>
          <li>Click Team.</li>
          <li>Click Add a team member.</li>
          <li>Enter the email address and select the Organization Profile you want to assign.</li>
          <li>Click Send Invitation.</li>
          <li>
            Your team members will receive an email invitation from Attractr. They need to accept
            the invitation to gain access. Any pending invites will appear in the Pending Invite
            section, where you can either resend or revoke the invitation.
          </li>
        </ul>
        <h3>I Wear Many Hats. Can I Create Multiple Organization Profiles?</h3>
        <p>
          Yes, you can create multiple organization profiles to manage various projects on Attractr.
          However, when submitting your pages, our team may review your association with the
          organization or production company to ensure authenticity. This could involve verifying
          information through your email domain, LinkedIn, website, or corporate documents.
        </p>
      </div>
    ),
  },
  {
    key: 'insights',
    label: 'Insights',
    topic: CREATOR_DASHBOARD,
    Html: (
      <div>
        <p>
          The Insights section provides essential statistics to help creators track their
          performance and audience engagement. The following metrics are currently available:
        </p>
        <ul>
          <li>Cumulative Revenues: Track earnings from donations and Live Room memberships.</li>
          <li>Total Members: See a breakdown of free and paid members.</li>
          <li>
            Live Room Episode Views: Monitor the number of views across all your Live Room episodes.
          </li>
          <li>
            Member's Database: Access your growing list of members, including contact details and
            subscription history.
          </li>
        </ul>
        <p>
          Please note that the current analytics features are basic, as we are in beta testing. We
          are actively developing more in-depth analytics tools to give production companies greater
          insight into their audience behavior, production performance, and content success.
        </p>
      </div>
    ),
  },
  {
    key: 'how-do-payouts-work',
    label: 'How Do Payouts Work?',
    topic: GETTING_PAID,
    Html: (
      <div>
        <p>Ready to see your earnings? Here’s a breakdown for Live Room owners:</p>
        <p>
          Your Payout = Membership Price - (Attractr Fee (10%) + Payment processing fee
          (2.9%+C$0.3))
        </p>
        <h3>Why Your Payout Might Differ from Total Revenue:</h3>
        <p>Several factors may impact your final payout amount:</p>
        <ul>
          <li>VAT: Value-added tax may apply depending on your region.</li>
          <li>
            Processing Fees: Some banks may charge their own fees; contact your financial
            institution for details.
          </li>
          <li>
            Currency Exchange: If your listing currency differs from your payout currency, an
            exchange fee may be applied.
          </li>
          <li>Adjustment Fees: Refunds or cancellations may reduce your earnings.</li>
        </ul>
        <p>
          Attractr uses Stripe Connect to manage payments, releasing monthly earnings on the 20th.
          Actual fund arrival depends on your bank and potential holidays or weekends.
        </p>
      </div>
    ),
  },
  {
    key: 'set-up-a-payout-method',
    label: 'Set Up a Payout Method',
    topic: GETTING_PAID,
    Html: (
      <div>
        <p>
          Creators launching a Live Room must set up a payout method on their Attractr profile.
          After saving your first Live Room, you’ll be prompted to add a payment method. Once added,
          this method will apply to all your Live Rooms.
        </p>
        <ul>
          <li>After saving your Live Room, you’ll be prompted to add a Payouts.</li>
          <li>Enter your bank account details.</li>
          <li>
            You’ll be redirected to Stripe’s settings—complete and submit the required information
            to verify your account.
          </li>
          <li>Once verified, your Live Room will be published.</li>
        </ul>
        <p>
          <strong>Note:</strong> Attractr won’t publish your Live Room until a payout method is
          configured.
        </p>
      </div>
    ),
  },
  {
    key: 'fees',
    label: 'Fees',
    topic: GETTING_PAID,
    Html: (
      <div>
        <p>
          To ensure we can continue to make Attractr better platform and solution , we charge
          Creators a 10% service fee. This is based on the total revenues from the membership and
          donation and it's automatically deducted from your payout. We don’t charge a fee to
          audiences.
        </p>
        <h3>What's included in the Attractr Fee:</h3>
        <ul>
          <li>Payout fee ($2 per month)</li>
          <li>Servers and operating cost</li>
          <li>Onboarding & empowerment programs</li>
          <li>VAT invoice fee</li>
        </ul>
        <h3>What's not included in the Attractr Fee:</h3>
        <ul>
          <li>Payment processing fee (2.9% + C$0.30)</li>
          <li>Currency conversion fee (1-2%)</li>
        </ul>
        <h3>Platform offers:</h3>
        <ul>
          <li>Unlimited live video chat (up to 100 attendees)</li>
          <li>Short clip uploads</li>
          <li>Insights</li>
          <li>Audience database</li>
          <li>Messaging</li>
          <li>Creator program and community access</li>
          <li>Marketing support</li>
        </ul>
        <h3>Creator's get the total earnings as follow (Example):</h3>
        <ul>
          <li>Amount Total Membership Fee: $20.00 </li>
          <li>Stripe Fee (2.9% + $0.30): -$0.88 </li>
          <li>Net Amount After Stripe Fee: $19.12</li>
          <li>Attractr’s Service Fee (10%): -$2.00 </li>
          <li>
            <strong>Creator’s Earnings: $17.12</strong>
          </li>
        </ul>
      </div>
    ),
  },
  {
    key: 'vat',
    label: 'VAT',
    topic: GETTING_PAID,
    Html: (
      <div>
        <p>
          As Attractr operates globally from Canada, tax obligations for Live Room owners (Creators)
          are generally determined by the location of the buyer (audience members).
        </p>
        <h3>Marketplace Fees for Live Room Owners:</h3>
        <ul>
          <li>
            <strong>Canadian Creators:</strong> If Attractr charges platform fees or commissions,
            GST/HST may apply to these fees.
          </li>
          <li>
            <strong>International Creators:</strong> Taxes on marketplace fees typically apply only
            to Canadian-based suppliers, in line with Canadian tax rules and international
            agreements.
          </li>
        </ul>
        <p>
          Creators are encouraged to consult a tax professional regarding their specific
          obligations, especially when operating across borders.
        </p>
      </div>
    ),
  },
  {
    key: 'receipts',
    label: 'Receipts',
    topic: GETTING_PAID,
    Html: (
      <div>
        <p>
          Attractr uses Stripe Connect to manage payments, ensuring a seamless and secure process
          for creators. Monthly earnings are released on the 20th of each month to the Creator of
          the Live Room. Please note that the actual arrival of funds depends on your bank’s
          processing times and may be delayed due to holidays or weekends. &nbsp; Once your earnings
          are processed, you will receive a monthly payout notification from Attractr, including a
          receipt link for download.
        </p>
        <h3>To view your earnings history: </h3>
        <ol>
          <li>Log in to your Attractr account. </li>
          <li>Navigate to the Creator Dashboard and click on Payment History under Settings. </li>
        </ol>
        <h3>Your earnings history will display the following details: </h3>
        <ul>
          <li>Processing Date</li>
          <li>Amount Charged</li>
          <li>Stripe fee</li>
          <li>Attracter fee</li>
          <li>Total earning</li>
          <li>Live Room that generated the payment</li>
          <li>Type of Purchase (e.g., subscription or donation)</li>
        </ul>
        <p>
          You will also receive a message from Attractr with a payout receipt link for your records.
        </p>
        <h3>Important Notes:</h3>
        <ul>
          <li>
            If your local currency differs from the Creator’s billing currency, currency conversions
            may appear on your bank statement.{' '}
          </li>
          <li>
            For any discrepancies or questions regarding your earnings, please contact Attractr
            Support.
          </li>
        </ul>
      </div>
    ),
  },
];

const frenchArticles = [
  {
    key: 'creating-an-account',
    label: 'Créer un compte',
    topic: PROFILE_AND_ACCOUNT_SETTINGS,
    Html: (
      <div>
        <h3 className={css.noTopMargin}>Créer un compte</h3>
        <p>
          Créer un compte Attractr est gratuit et facile. Il suffit de visiter{' '}
          <ExternalLink href="https://beta.attractr.live">beta.attractr.live</ExternalLink>, de
          cliquer sur Inscription et de suivre les instructions étape par étape.
        </p>
        <h3>Choisir le type de profil</h3>
        <p>
          Lors de l'inscription, choisissez votre type de profil: Audience ou Créateur. Le profil
          Créateur est conçu pour les compagnies de production, les artistes, les créateurs et les
          marketeurs souhaitant créer et gérer une Page de Spectacle et une Salle en Direct, ainsi
          qu'accéder au Tableau de Bord Créateur. Si vous choisissez un profil Créateur, vous pouvez
          facilement passer du mode Créateur au mode Audience à tout moment sans avoir à créer un
          nouveau compte. Si vous ne prévoyez pas de créer une Page de Spectacle ou une Salle en
          Direct, choisissez simplement le profil Audience.
        </p>
        <h3>Inscription</h3>
        <p>Vous pouvez vous inscrire en utilisant l'une des options suivantes:</p>
        <ul>
          <li>Adresse e-mail</li>
          <li>Compte Facebook</li>
          <li>Compte Google</li>
        </ul>
        <p>
          Une fois le processus d'inscription terminé, vous recevrez un e-mail de vérification dans
          votre boîte de réception enregistrée. Assurez-vous de cliquer sur le lien de vérification
          dans les 48 heures pour confirmer votre e-mail. Si vous ne vérifiez pas dans ce délai, le
          lien expirera. Cependant, vous pouvez facilement en demander un nouveau en cliquant sur
          Renvoyer l'e-mail de vérification sur la page de connexion.
        </p>
        <p>
          Après votre inscription, vous devrez fournir des informations supplémentaires avant de
          pouvoir accéder pleinement à toutes les fonctionnalités telles que rejoindre des
          adhésions, réserver des événements en Salle en Direct ou contacter des Créateurs. Pour
          finaliser la configuration de votre compte, accédez à la section Compte.
        </p>
        <p>Au minimum, nous aurons besoin de:</p>
        <ul>
          <li>Nom complet</li>
          <li>Adresse e-mail</li>
          <li>Photo de profil</li>
          <li>Numéro de téléphone</li>
          <li>Date de naissance</li>
          <li>Localisation</li>
          <li>Informations de paiement</li>
        </ul>
        <h3>Informations publiques du compte</h3>
        <p>
          Vos informations privées sont gérées conformément à notre Politique de Confidentialité,
          garantissant qu'elles ne sont pas partagées avec les Créateurs ou les invités. Les seuls
          détails visibles sur votre profil public sont:
        </p>
        <ul>
          <li>Prénom</li>
          <li>Statut de vérification (E-mail et Téléphone)</li>
        </ul>
        <p>
          Soyez assuré que vos informations de paiement sont complètement privées et uniquement
          visibles par vous.
        </p>
      </div>
    ),
  },
  {
    key: 'change-your-password',
    label: 'Changer votre mot de passe',
    topic: PROFILE_AND_ACCOUNT_SETTINGS,
    Html: (
      <div>
        <h3 className={css.noTopMargin}>Modifier votre mot de passe</h3>
        <p>
          Vous avez oublié votre mot de passe ou vous avez des difficultés à vous connecter ? Voici
          comment modifier ou réinitialiser votre mot de passe.
        </p>
        <h3>Comment modifier votre mot de passe:</h3>
        <ul>
          <li>Accédez à la section Compte et sélectionnez Paramètres du mot de passe.</li>
          <li>Saisissez un nouveau mot de passe.</li>
          <li>Pour finaliser le changement, saisissez votre mot de passe actuel*.</li>
          <li>Cliquez sur Enregistrer les modifications.</li>
        </ul>
        <h3>Réinitialiser votre mot de passe:</h3>
        <p>Si vous ne vous souvenez pas de votre mot de passe actuel:</p>
        <ul>
          <li>Cliquez sur Envoyer les instructions de réinitialisation.</li>
          <li>
            Vous recevrez un e-mail d'Attractr contenant les instructions pour réinitialiser votre
            mot de passe.
          </li>
          <li>Cliquez sur le lien dans l'e-mail pour définir un nouveau mot de passe.</li>
          <li>Cliquez sur Réinitialiser le mot de passe pour finaliser le processus.</li>
        </ul>
      </div>
    ),
  },
  {
    key: 'changing-your-email-address',
    label: 'Changer votre adresse e-mail',
    topic: PROFILE_AND_ACCOUNT_SETTINGS,
    Html: (
      <div>
        <h3 className={css.noTopMargin}>Modifier votre adresse e-mail</h3>
        <p>
          Vous pouvez facilement modifier l'adresse e-mail associée à votre compte Attractr.
          Veuillez noter que chaque adresse e-mail ne peut être liée qu'à un seul compte Attractr à
          la fois.
        </p>
        <h3>Comment modifier votre adresse e-mail:</h3>
        <ul>
          <li>Accédez à la section Compte et sélectionnez Détails de contact.</li>
          <li>Saisissez votre nouvelle adresse e-mail dans le champ Votre adresse e-mail.</li>
          <li>Entrez votre mot de passe actuel pour la vérification de sécurité.</li>
          <li>Cliquez sur Enregistrer les modifications.</li>
          <li>Sélectionnez Renvoyer l'e-mail de vérification.</li>
          <li>
            Ouvrez l'e-mail envoyé par Attractr et cliquez sur le lien de vérification pour
            confirmer votre nouvelle adresse e-mail.
          </li>
        </ul>
        <p>
          Important: Votre nouvelle adresse e-mail ne sera pas mise à jour tant que vous n'aurez pas
          terminé le processus de vérification.
        </p>
      </div>
    ),
  },
  {
    key: 'create-and-edit-your-profile',
    label: 'Créer et modifier votre profil',
    topic: PROFILE_AND_ACCOUNT_SETTINGS,
    Html: (
      <div>
        <h3 className={css.noTopMargin}>Créer et modifier votre profil</h3>
        <p>
          Chez Attractr, nous croyons en l’importance de créer des connexions authentiques et
          significatives entre les Créateurs et les Audiences. Pour aider à établir ces relations,
          prenez un moment pour compléter votre profil avec des informations clés.
        </p>
        <h3>Comment compléter votre profil:</h3>
        <ul>
          <li>Ajoutez votre nom complet.</li>
          <li>Rédigez une courte biographie.</li>
          <li>Indiquez votre localisation.</li>
          <li>Sélectionnez votre ou vos langue(s).</li>
          <li>Ajoutez le lien vers votre compte Instagram.</li>
        </ul>
        <p>N’oubliez pas de télécharger une photo de profil pour personnaliser votre compte.</p>
        <h3>Directives pour la photo de profil:</h3>
        <p>
          Nous recommandons vivement à tous les membres de télécharger une photo de profil. Pour une
          meilleure qualité:
        </p>
        <ul>
          <li>Votre photo doit mesurer au moins 320 pixels de large et 320 pixels de haut.</li>
          <li>L’image sera recadrée en cercle.</li>
          <li>
            Pour des résultats optimaux, nous suggérons d’utiliser un fichier PNG, bien que les
            fichiers JPEG soient également pris en charge.
          </li>
        </ul>
        <h3>Ce qui sera affiché sur votre profil:</h3>
        <h4>Pour les Audiences</h4>
        <ul>
          <li>Badges utilisateur</li>
          <li>Scores d'engagement totaux (Statistiques)</li>
          <li>Avis</li>
          <li>Listes de visionnage</li>
          <li>Artistes les plus engageants ce mois-ci</li>
          <li>Abonnements</li>
        </ul>
        <h4>Pour les Créateurs</h4>
        <ul>
          <li>Biographie</li>
          <li>Détails personnels</li>
          <li>Crédits de spectacles</li>
          <li>Live Rooms</li>
          <li>Collaborateurs fréquents</li>
        </ul>
      </div>
    ),
  },
  {
    key: 'user-badge',
    label: 'Badge utilisateur',
    topic: PROFILE_AND_ACCOUNT_SETTINGS,
    Html: (
      <div>
        <h3 className={css.noTopMargin}>Badge utilisateur</h3>
        <p>
          Indicateurs visuels attribués aux membres pour leurs réalisations ou rôles au sein de la
          communauté.
        </p>
        <h3>Qu’est-ce qu’un badge utilisateur sur Attractr ?</h3>
        <p>
          Les badges utilisateurs sur Attractr sont une forme de reconnaissance pour la
          participation active et l'engagement des membres dans notre communauté dynamique. Ces
          badges sont conçus pour récompenser et encourager les interactions positives, telles que
          l'évaluation et la critique de spectacles, la publication de commentaires, et la
          participation aux activités des Live Rooms, y compris les dons et les abonnements aux
          Créateurs.
        </p>
        <p>Les badges peuvent être obtenus pour diverses actions et réalisations, telles que:</p>
        <ul>
          <li>Compléter un certain nombre d’évaluations et de critiques.</li>
          <li>S’abonner à un nombre spécifique d’événements Live Room.</li>
          <li>Contribuer par des dons aux Créateurs.</li>
        </ul>
        <p>
          Les badges ajoutent un élément ludique et engageant pour motiver les membres à participer,
          tout en identifiant les contributeurs actifs et précieux au sein de la communauté.
          Affichés sur votre profil, les badges introduisent un aspect de gamification sur la
          plateforme, promouvant un sentiment d'accomplissement. Ils favorisent l'esprit
          communautaire, encouragent les comportements positifs et augmentent l'engagement global.
        </p>
        <h3>Comment obtenir des badges utilisateurs:</h3>
        <p>
          Pour gagner des badges, participez activement à la communauté ! Voici quelques moyens de
          commencer:
        </p>
        <ul>
          <li>Évaluez et laissez des critiques sur les spectacles.</li>
          <li>Abonnez-vous aux Live Rooms et soutenez les Créateurs.</li>
          <li>Invitez des amis à participer aux événements Live Room.</li>
        </ul>
        <p>
          Plus vous contribuez et vous engagez, plus vous recevrez de reconnaissance—de la part de
          la communauté et des Créateurs.
        </p>
        <h3>Système de points</h3>
        <ul>
          <li>Dépenses d'abonnement Live Room: 1 $ = 10 points</li>
          <li>Dépenses de dons: 1 $ = 10 points</li>
          <li>
            Nombre d’avis et d’évaluations effectués: 1 avis = 20 points, 1 évaluation = 5 points
          </li>
          <li>Nombre d’épisodes de Live Room suivis: 1 épisode de Live Room = 10 points</li>
          <li>Nombre de commentaires sur les Live Rooms: 1 commentaire = 5 points</li>
          <li>Nombre d’amis invités pour les Live Rooms: 1 ami invité = 10 points</li>
        </ul>
        <h3>Types de niveaux:</h3>
        <ul>
          {Object.values(config.userTiers).map(tier => {
            const tierName = tier.id.charAt(0).toUpperCase() + tier.id.slice(1);
            return (
              <li key={tier.id}>
                <BadgeTier className={css.badgeIcon} tierId={tier.id} title={tierName} size={24} />{' '}
                <strong>{tierName}:</strong> {tier.min}
                {tier.max === Infinity ? '+' : ` - ${tier.max}`} points
              </li>
            );
          })}
        </ul>
        <h3>Badges spéciaux</h3>
        <ul>
          <li>
            <BadgeStuff className={css.badgeIcon} /> <strong>Équipe Attractr:</strong> Notre équipe.
          </li>
          <li>
            <BadgeBetaTester className={css.badgeIcon} /> <strong>Meilleur testeur:</strong>{' '}
            Attribué aux meilleurs bêta-testeurs qui contribuent à améliorer Attractr grâce à des
            retours précieux et à leur participation.
          </li>
          <li>
            <BadgeTopReviewer className={css.badgeIcon} /> <strong>Meilleur critique:</strong>{' '}
            Classé parmi les 1 % des membres Attractr ayant contribué au plus grand nombre d’avis.
          </li>
        </ul>
      </div>
    ),
  },
  {
    key: 'creator-profile',
    label: 'Profil du créateur',
    topic: PROFILE_AND_ACCOUNT_SETTINGS,
    Html: (
      <div>
        <h3 className={css.noTopMargin}>Profil Créateur</h3>
        <p>
          Lorsque vous vous inscrivez en tant que Créateur, vous disposez de deux types de profils:
          un Profil Audience et un Profil Créateur. Votre Profil Créateur sert de portfolio mettant
          en valeur votre travail. Il regroupe tous les crédits de spectacles qui vous ont été
          attribués ou ajoutés, offrant une présentation complète de vos contributions et
          réalisations à travers divers projets. Ce profil permet aux spectateurs et aux
          professionnels de l'industrie d'explorer votre parcours créatif et de s'engager avec votre
          travail.
        </p>
        <h3>Accès à votre Profil Créateur:</h3>
        <p>
          Pour consulter votre Profil Créateur, cliquez simplement sur votre profil dans la section
          des crédits de n’importe quelle Live Room ou Production Page. Actuellement, l'édition
          directe n'est pas disponible, mais nous travaillons activement à élargir ces
          fonctionnalités pour offrir bientôt une personnalisation accrue.
        </p>
      </div>
    ),
  },
  {
    key: 'deleting-your-account',
    label: 'Supprimer votre compte',
    topic: PROFILE_AND_ACCOUNT_SETTINGS,
    Html: (
      <div>
        <h3 className={css.noTopMargin}>Suppression de votre compte</h3>
        <p>
          Alors que nous construisons une base de connaissances complète pour les arts de la scène,
          nous concevons soigneusement le processus de suppression de compte afin de garantir que
          les informations publiques liées aux Production Pages restent préservées et sécurisées.
          Une fois finalisée, cette fonctionnalité sera intégrée à la plateforme. En attendant, si
          vous souhaitez supprimer votre compte, veuillez nous contacter à
          <a href="mailto:team@attractr.live">team@attractr.live</a> pour demander la suppression de
          votre compte.
        </p>
      </div>
    ),
  },
  {
    key: 'what-is-a-live-room',
    label: "Qu'est-ce qu'une Live Room?",
    topic: LIVE_ROOM,
    Html: (
      <div>
        <h3 className={css.noTopMargin}>Qu'est-ce qu'une Live Room ?</h3>
        <p>
          Une Live Room est un espace virtuel interactif où les Créateurs, y compris les artistes,
          les équipes de production et les artisans, invitent les spectateurs à découvrir le
          processus créatif derrière leurs spectacles. C’est l’occasion d’aller au-delà de la scène
          et d’accéder à un contenu exclusif, des diffusions en direct et des interactions directes
          avec les créateurs. Que ce soit pour donner votre avis afin d’influencer un projet,
          profiter d’une conversation décontractée après une performance ou assister à des
          répétitions en direct, les Live Rooms vous rapprochent du cœur de la production.
        </p>
        <p>
          Les Live Rooms proposent des expériences gratuites ou basées sur un abonnement, avec des
          événements programmés à l’avance. Vous pouvez y participer facilement via un ordinateur,
          une tablette ou un mobile, et vous plonger dans les histoires inédites des spectacles,
          tout en restant confortablement chez vous.
        </p>
        <h3>Enrichissez votre expérience d’achat de billets:</h3>
        <p>
          Les Live Rooms sont présentées sur la Production Page d’un spectacle, vous permettant
          d'explorer le projet plus en profondeur avant de décider d’y assister en personne. Ce
          n’est pas seulement une question d’apprendre davantage, mais aussi de tisser un lien
          personnel avec les artistes et les créateurs, de découvrir leur parcours et de les
          soutenir directement. En devenant membre, vous contribuez à concrétiser ces visions
          créatives tout en profitant d’avantages uniques et de contenus bien au-delà de ce qu’offre
          une page événementielle traditionnelle.
        </p>
      </div>
    ),
  },
  {
    key: 'booking-live-room-episodes',
    label: 'Réserver des épisodes de Live Room',
    topic: LIVE_ROOM,
    Html: (
      <div>
        <h3 className={css.noTopMargin}>Réserver des épisodes de Live Room</h3>
        <p>
          Une Live Room est un espace dédié où les Créateurs, tels que des artistes, des équipes de
          production ou des artisans, partagent des contenus en coulisses, interagissent en direct
          ou organisent des moments de détente après les spectacles.
        </p>
        <h3>Comment réserver et participer à un épisode de Live Room:</h3>
        <ul>
          <li>
            Choisissez un épisode: Sélectionnez l’épisode auquel vous souhaitez assister et cliquez
            sur RSVP.
          </li>
          <li>
            Rejoignez gratuitement: Vous devez devenir membre gratuit pour réserver tout épisode
            gratuit de Live Room. Cliquez sur Rejoindre gratuitement pour continuer.
          </li>
          <li>
            Ajoutez un message personnel (optionnel): Sur la page suivante, vous pouvez écrire un
            message personnel au Créateur avant de cliquer sur Confirmer la réservation.
          </li>
          <li>
            Recevez votre lien d’épisode: Un lien vers l’épisode sera envoyé à votre boîte de
            réception.
          </li>
          <li>
            Participez à l’épisode: Cliquez sur le lien pour rejoindre l’épisode de Live Room.
          </li>
        </ul>
        <p>
          Vos épisodes réservés seront également ajoutés à votre calendrier pour un accès facile.
        </p>
        <p>
          Nous utilisons Whereby pour les épisodes de Live Room, accessibles via un ordinateur, une
          tablette ou un mobile en cliquant sur le lien de l’épisode ("Lancer dans le navigateur").
          L’application Whereby est disponible sur iOS.
        </p>
        <p>
          Les épisodes marqués d’une étoile (<Icon name="premium" size={16} className={css.star} />)
          sont réservés aux membres. Pour plus de détails, consultez Comment s’abonner à un
          abonnement de Live Room.
        </p>
      </div>
    ),
  },
  {
    key: 'booking-live-room-episodes-for-groups-or-guests',
    label: 'Réserver des épisodes de Live Room pour des groupes ou des invités',
    topic: LIVE_ROOM,
    Html: (
      <div>
        <h3>Épisodes gratuits de Live Room</h3>
        <p>
          Vous pouvez partager des épisodes gratuits de Live Room avec votre famille, vos amis ou
          vos camarades de classe. Chaque épisode peut accueillir jusqu'à 100 spectateurs, et vous
          pouvez inviter jusqu'à 10 invités lors de la réservation.
        </p>
        <h3>Comment réserver des épisodes de Live Room pour des groupes ou des invités:</h3>
        <ul>
          <li>
            Cliquez sur Invités dans le panneau de planification et sélectionnez la taille du
            groupe.
          </li>
          <li>Choisissez un épisode et cliquez sur RSVP.</li>
          <li>
            Optionnellement, écrivez un message personnel au Créateur et cliquez sur Confirmer la
            réservation.
          </li>
          <li>
            Sur l'écran de confirmation, cliquez sur Ajouter cet épisode à votre calendrier et
            invitez vos invités via l'invitation calendrier.
          </li>
          <li>Vos invités recevront un lien Whereby pour rejoindre l'épisode de Live Room.</li>
        </ul>
        <p>
          <strong>Remarque:</strong> Tous les invités doivent s'inscrire sur Attractr pour accéder à
          l'épisode de Live Room.
        </p>
        <h3>Épisodes de Live Room réservés aux membres</h3>
        <p>
          Les épisodes de Live Room réservés aux membres sont exclusifs aux abonnés. Actuellement,
          les membres ne peuvent pas inviter d'invités, mais nous travaillons à introduire cette
          fonctionnalité prochainement, ainsi que d'autres avantages passionnants liés aux
          abonnements.
        </p>
      </div>
    ),
  },
  {
    key: 'contacting-creators',
    label: 'Contacter les créateurs',
    topic: LIVE_ROOM,
    Html: (
      <div>
        <p>
          Que vous ayez une question sur une Live Room ou sur le projet d'un Créateur, ou que vous
          souhaitiez laisser un message personnel de soutien, contacter un Créateur commence par un
          simple message. Vos fils de messages sont stockés dans votre boîte de réception, où vous
          pouvez gérer toutes vos communications et historiques d'activités.
        </p>
        <h3>Moyens d'envoyer des messages aux Créateurs:</h3>
        <ul>
          <li>
            Accédez à votre boîte de réception et sélectionnez un fil existant ou commencez-en un
            nouveau.
          </li>
          <li>Visitez une Live Room et cliquez sur Contacter le Créateur.</li>
          <li>
            Pour des questions sur un abonnement ou un soutien, cliquez sur Abonnement ou Don, puis
            sur Contacter le Créateur.
          </li>
        </ul>
        <h3>Laisser une note personnelle:</h3>
        <ul>
          <li>
            Lors de la réservation d'un épisode de Live Room ou de l'abonnement à un service, vous
            pouvez inclure un message personnel à l'intention du Créateur. Cela aide à établir une
            connexion directe, et nous encourageons à laisser une note réfléchie pour motiver le
            Créateur.
          </li>
        </ul>
      </div>
    ),
  },
  {
    key: 'managing-live-room-episodes-in-calendar',
    label: 'Gérer les épisodes de Live Room dans le calendrier',
    topic: LIVE_ROOM,
    Html: (
      <div>
        <p>
          Restez à jour avec tous vos épisodes de Live Room. Étant donné qu'il s'agit d'événements
          en ligne en direct, votre participation responsable motive les Créateurs que vous
          soutenez.
        </p>
        <h3>Comment gérer vos épisodes de Live Room:</h3>
        <ul>
          <li>Tous les épisodes réservés sont disponibles dans votre Calendrier.</li>
          <li>
            Cliquez sur chaque épisode dans le Calendrier pour accéder au lien de l'événement et aux
            détails.
          </li>
          <li>
            Ajustez le fuseau horaire dans votre Calendrier pour afficher les épisodes à votre heure
            locale.
          </li>
          <li>
            Si vous êtes en retard ou devez annuler, informez le Créateur en lui envoyant un message
            via votre Boîte de réception.
          </li>
        </ul>
      </div>
    ),
  },
  {
    key: 'cancel-or-change-live-room-episodes',
    label: 'Annuler ou modifier des épisodes de Live Room',
    topic: LIVE_ROOM,
    Html: (
      <div>
        <p>
          À l'heure actuelle, il n'est pas possible d'annuler un épisode de Live Room réservé.
          Cependant, nous vous demandons gentiment d'informer le Créateur de votre absence en lui
          envoyant une note personnelle. Nous travaillons en continu pour améliorer l'expérience des
          Live Rooms et prévoyons d'ajouter plus de fonctionnalités prochainement.
        </p>
        <p>
          Vos retours sont inestimables pour nous aider à progresser ! Rejoignez notre Communauté
          pour partager vos idées et contribuer à façonner les prochaines mises à jour.
        </p>
      </div>
    ),
  },
  {
    key: 'subscribing-to-live-room-membership',
    label: "S'abonner à l'abonnement Live Room",
    topic: LIVE_ROOM,
    Html: (
      <div>
        <p>
          Soutenir les Créateurs et leurs spectacles grâce à un abonnement vous donne un accès
          exclusif à leurs projets, contenus en coulisses, et une compréhension plus approfondie de
          leur processus créatif. Voici comment commencer:
        </p>
        <h3>Étapes simples:</h3>
        <ul>
          <li>
            Découvrez un Spectacle ou un Créateur: Trouvez un spectacle ou un Créateur que vous
            souhaitez explorer davantage.
          </li>
          <li>Abonnez-vous: Cliquez sur le bouton S'abonner dans leur Live Room.</li>
          <li>
            Consultez les avantages: Vérifiez les niveaux d'abonnement disponibles et leurs
            avantages, puis cliquez sur Gérer.
          </li>
          <li>
            Finalisez le paiement: Saisissez les informations de votre carte de crédit sur la page
            de paiement et cliquez sur Confirmer le paiement.
          </li>
          <li>
            Profitez d’un accès exclusif: Débloquez des contenus réservés aux membres tout en
            soutenant le travail du Créateur !
          </li>
        </ul>
        <h3>Épisodes de Live Room réservés aux membres:</h3>
        <p>
          Les épisodes marqués d'une étoile (<Icon name="premium" size={16} className={css.star} />)
          sont réservés aux membres, accessibles aux abonnés.
        </p>
        <h3>Facturation unique:</h3>
        <p>
          Les abonnements aux Live Rooms fonctionnent sur un système de facturation unique, valable
          pour une durée spécifique. Assurez-vous de vérifier la période d'abonnement avant de
          procéder à l'achat.
        </p>
      </div>
    ),
  },
  {
    key: 'how-to-receive-membership-perks',
    label: "Comment recevoir les avantages de l'abonnement",
    topic: LIVE_ROOM,
    Html: (
      <div>
        <p>
          Chaque Créateur définit ses propres avantages d'abonnement, qui peuvent varier selon les
          Live Rooms. Tous les abonnements incluent l'accès aux épisodes réservés aux membres. Pour
          consulter les avantages, les niveaux d'abonnement et la durée, cliquez sur le bouton
          Gérer. Si vous avez des questions, n'hésitez pas à contacter directement le Créateur.
        </p>
        <h3>Types d’avantages</h3>
        <ul>
          <li>Événements en direct réservés aux membres (par défaut)</li>
          <li>Codes promo pour les billets*</li>
          <li>Réductions sur le merchandising*</li>
          <li>Remerciements publics</li>
          <li>Accès anticipé aux billets*</li>
          <li>Crédits sur les spectacles</li>
          <li>Invitations à des événements spéciaux*</li>
        </ul>
        <p>*Ces avantages seront envoyés directement par message par le Créateur.</p>
        <h3>Accès aux épisodes réservés aux membres dans les Live Rooms:</h3>
        <p>
          Les épisodes marqués d'une étoile (<Icon name="premium" size={16} className={css.star} />)
          sont exclusifs aux membres. Cliquez simplement sur RSVP, confirmez votre réservation, et
          vous êtes prêt à y assister.
        </p>
        <h3>Types d’épisodes réservés aux membres:</h3>
        <p>
          Les épisodes réservés aux membres peuvent inclure des sessions de questions/réponses, des
          répétitions exclusives, des visites virtuelles des coulisses, et bien plus encore, selon
          la créativité du Créateur. Choisissez les épisodes en fonction de leur titre et
          description pour profiter d'une variété d'expériences uniques.
        </p>
        <h3>Recevoir d’autres avantages:</h3>
        <p>
          Une fois que vous avez acheté un abonnement, un fil de discussion s’ouvrira entre vous et
          le Créateur. Tous les avantages supplémentaires, tels que les codes promo et les offres
          spéciales, vous seront envoyés directement par message.
        </p>
      </div>
    ),
  },
  {
    key: 'creator-is-not-delivering-benefits',
    label: 'Le créateur ne fournit pas les avantages',
    topic: LIVE_ROOM,
    Html: (
      <div>
        <div>
          <p>
            Attractr ne gère pas directement la livraison des avantages d'abonnement, mais voici
            quelques étapes à suivre si votre Créateur ne répond pas à vos attentes:
          </p>
          <h3>Envoyez un message au Créateur:</h3>
          <p>
            Si vos avantages sont retardés, contactez d'abord directement le Créateur. Il pourrait y
            avoir une raison valable au retard, ou il pourrait s'agir d'un simple oubli ou problème
            de livraison.
          </p>
          <h3>Demandez un remboursement:</h3>
          <p>
            Si vous préférez ne pas attendre les avantages, vous pouvez demander un remboursement
            directement auprès du Créateur. Les remboursements complets sont disponibles jusqu'au 20
            du mois de votre abonnement, à condition que vous n'ayez pas accédé à du contenu réservé
            aux membres. Pour demander un remboursement, veuillez contacter
            <a href="mailto:team@attractr.live">team@attractr.live</a> pour obtenir de l'aide.
          </p>
          <h3>Annulez votre abonnement:</h3>
          <p>
            Si le problème persiste, contactez{' '}
            <a href="mailto:team@attractr.live">team@attractr.live</a> pour obtenir de l'aide afin
            d'annuler votre abonnement après avoir tenté de résoudre le problème avec le Créateur.
          </p>
        </div>

        <div>
          <p>
            Vous pouvez consulter les détails de votre abonnement sur la page Abonnements. Pour
            vérifier votre statut:
          </p>
          <ul>
            <li>Allez dans le menu et cliquez sur Abonnements.</li>
            <li>Vous y trouverez les informations suivantes:</li>
            <ul>
              <li>Les Live Rooms auxquelles vous êtes abonné(e)</li>
              <li>Le niveau d'abonnement</li>
              <li>Le statut actuel</li>
              <li>La date d'expiration</li>
            </ul>
          </ul>
          <p>
            Vous recevrez également un message de rappel quelques jours avant l'expiration de votre
            abonnement.
          </p>
        </div>
      </div>
    ),
  },
  {
    key: 'what-is-donate-on-live-room',
    label: `Qu'est-ce que "Donner" sur Live Room`,
    topic: LIVE_ROOM,
    Html: (
      <div>
        <p>
          Attractr vous permet de soutenir directement des projets créatifs en faisant un don via la
          fonction Faire un don. Les dons sont une contribution ponctuelle qui aide les Créateurs à
          concrétiser leurs visions.
        </p>
        <h3>Qui peut faire un don:</h3>
        <ul>
          <li>
            Toute personne possédant un compte Attractr peut faire un don, même sans s'abonner à la
            Live Room.
          </li>
        </ul>
        <h3>Comment faire un don:</h3>
        <ul>
          <li>Visitez la Live Room que vous souhaitez soutenir.</li>
          <li>Cliquez sur Faire un don et sélectionnez le montant de votre don.</li>
          <li>
            Cliquez sur Faire un don, saisissez vos informations de paiement, et confirmez votre
            paiement.
          </li>
        </ul>
        <p>Pour toute question, n'hésitez pas à contacter le Créateur avant de faire un don.</p>
      </div>
    ),
  },
  {
    key: 'how-to-leave-comments-on-live-room',
    label: 'Comment laisser des commentaires sur Live Room',
    topic: LIVE_ROOM,
    Html: (
      <div>
        <p>
          Faites entendre votre voix ! Après avoir participé à un épisode de Live Room, vous pouvez
          laisser des commentaires et des avis pour partager vos impressions et vous connecter avec
          la communauté.
        </p>
        <h3>Comment laisser un commentaire:</h3>
        <ul>
          <li>
            Pour laisser un commentaire, vous devez avoir un compte Attractr et être abonné à la
            Live Room, soit via une adhésion gratuite ou payante. Une fois abonné, vous aurez accès
            à la section Discourse de la Live Room, où vous pourrez laisser des commentaires et
            interagir avec les autres membres.
          </li>
        </ul>
      </div>
    ),
  },
  {
    key: 'how-to-leave-review-ratings-on-show-pages',
    label: 'Comment laisser des avis et des évaluations sur les pages de spectacle',
    topic: SHOW_PAGE,
    Html: (
      <div>
        <h3>Qu'est-ce que les évaluations Attractr ?</h3>
        <p>
          Les utilisateurs enregistrés sur Attractr peuvent évaluer chaque page de spectacle dans la
          base de données sur une échelle de 1 à 10. Ces votes individuels sont agrégés en une seule
          note Attractr, qui est affichée sur la page principale du spectacle. Vous pouvez mettre à
          jour votre évaluation aussi souvent que vous le souhaitez, mais chaque nouvelle évaluation
          remplace la précédente—c'est donc un vote par spectacle et par utilisateur.
        </p>
        <h3>Comment puis-je voir mes évaluations ?</h3>
        <p>
          Vous pouvez consulter vos évaluations directement sur les pages de spectacle individuelles
          et sur votre page de profil.
        </p>
        <h3>Comment puis-je laisser un avis ?</h3>
        <p>Pour laisser un avis sur le site web de bureau:</p>
        <ul>
          <li>
            Accédez à la page du spectacle, faites défiler vers le bas et cliquez sur + Avis dans la
            section Avis des utilisateurs.
          </li>
          <li>Un formulaire vous sera présenté où vous pourrez:</li>
          <ul>
            <li>Mettre à jour ou ajouter votre note étoilée</li>
            <li>Rédiger un avis détaillé (assurez-vous de mentionner les éventuels spoilers).</li>
          </ul>
          <li>Une fois terminé, cliquez sur Publier votre avis.</li>
        </ul>
        <p>
          <strong>Remarque:</strong> La longueur minimale des avis est de 600 caractères. Les avis
          remplis de texte non pertinent seront rejetés pour maintenir la qualité.
        </p>
        <h3>Comment puis-je voir mon avis ?</h3>
        <p>
          Vos évaluations sont disponibles sur la page du spectacle et sur votre page de profil.
        </p>
        <p>
          Nous développons une nouvelle fonctionnalité qui permettra aux spectateurs de collecter et
          de suivre facilement ce qu'ils ont regardé, évalué ou ajouté à leur liste de visionnage,
          le tout dans un seul endroit pratique. Restez à l'écoute !
        </p>
      </div>
    ),
  },
  {
    key: 'can-i-buy-a-ticket-from-show-page',
    label: 'Puis-je acheter un billet depuis la page de spectacle?',
    topic: SHOW_PAGE,
    Html: (
      <div>
        <p>
          Non, Attractr ne vend pas directement de billets via notre plateforme. Cependant, vous
          pouvez facilement trouver des billets en cliquant sur le bouton "Trouver un billet", qui
          vous redirigera vers la page de billetterie correspondante pour l'événement choisi.
        </p>
      </div>
    ),
  },
  {
    key: 'can-i-bookmark-a-show',
    label: 'Puis-je ajouter un spectacle à mes favoris?',
    topic: SHOW_PAGE,
    Html: (
      <div>
        <p>
          Oui ! Vous pouvez facilement ajouter des pages de spectacle à vos favoris en cliquant sur
          l'icône + en forme de ruban sur la page du spectacle. Cela enregistre le spectacle dans
          votre liste de visionnage, vous permettant de suivre les événements et spectacles à venir
          qui vous intéressent. Accédez à Ma liste de visionnage depuis la page d'accueil pour ne
          jamais manquer les prochaines nouveautés !
        </p>
      </div>
    ),
  },
  {
    key: 'paying-for-membership',
    label: "Payer pour l'abonnement",
    topic: PAYMENTS_AND_REFUNDS,
    Html: (
      <div>
        <p>
          Soutenir les Créateurs par le biais d'un abonnement contribue à concrétiser leurs projets.
          Après avoir sélectionné un forfait d'abonnement, suivez ces étapes pour finaliser votre
          paiement:
        </p>
        <h3>Méthode de paiement:</h3>
        <ul>
          <li>
            Nous acceptons les cartes de crédit (Visa, MasterCard, Amex, JCB et cartes de débit
            traitées comme des crédits).
          </li>
          <li>Saisissez les informations de votre carte et cliquez sur Confirmer le paiement.</li>
          <li>
            Pour enregistrer votre carte pour de futures transactions, cochez la case Enregistrer
            les informations de la carte ou enregistrez-la plus tard dans Compte `{'>'}` Méthodes de
            paiement.
          </li>
        </ul>
        <h3>Paiement unique:</h3>
        <ul>
          <li>
            Les abonnements sont facturés en tant que paiement unique et sont valables jusqu'à la
            date de fin spécifiée.
          </li>
          <li>Le paiement est traité immédiatement après confirmation.</li>
        </ul>
        <h3>Abonnements multiples:</h3>
        <ul>
          <li>
            Vous pouvez souscrire à plusieurs abonnements sans frais supplémentaires ni récurrents
            pendant la durée de chaque abonnement.
          </li>
        </ul>
        <h3>Contacter les Créateurs:</h3>
        <ul>
          <li>
            Si vous avez des questions ou besoin d'assistance, vous pouvez contacter directement les
            Créateurs via votre Messagerie en sélectionnant le fil de discussion pertinent depuis
            leur Live Room.
          </li>
        </ul>
      </div>
    ),
  },
  {
    key: 'your-donation-supports-creators',
    label: 'Votre don soutient les créateurs',
    topic: PAYMENTS_AND_REFUNDS,
    Html: (
      <div>
        <p>
          Votre don aide les Créateurs à poursuivre et faire évoluer leurs projets. Les montants de
          don disponibles sont 25 $, 50 $, 100 $, 200 $ ou 500 $.
        </p>
        <h3>Méthode de paiement:</h3>
        <ul>
          <li>
            Nous acceptons les cartes de crédit (Visa, MasterCard, Amex, JCB et cartes de débit
            traitées comme des crédits).
          </li>
          <li>Saisissez les informations de votre carte et cliquez sur Confirmer le paiement.</li>
          <li>
            Pour enregistrer votre carte pour une utilisation future, cochez la case Enregistrer les
            informations de la carte ou ajoutez-la plus tard dans Compte > Méthodes de paiement.
          </li>
          <li>Votre paiement est traité et facturé immédiatement après confirmation.</li>
        </ul>
        <h3>Avantages pour les donateurs:</h3>
        <p>
          Si le Créateur propose des avantages pour les donateurs (tels que des remerciements, des
          sessions privées ou un accès anticipé), ils seront envoyés via messagerie.
        </p>
        <h3>Contacter les Créateurs:</h3>
        <p>
          Vous pouvez contacter les Créateurs directement via votre Messagerie en sélectionnant le
          fil de discussion Live Room pertinent.
        </p>
      </div>
    ),
  },
  {
    key: 'cancel-and-refund',
    label: 'Annuler et rembourser',
    topic: PAYMENTS_AND_REFUNDS,
    Html: (
      <div>
        <p>
          Attractr est une plateforme qui facilite les paiements des membres vers les Créateurs. Une
          fois qu'un paiement est traité, il est envoyé au solde du Créateur. Par conséquent, si
          vous souhaitez annuler en raison d’avantages non livrés, nous vous encourageons à
          contacter d'abord le Créateur pour résoudre le problème.
        </p>
        <h3>Annuler un épisode Live Room programmé:</h3>
        <p>
          Si un imprévu survient et que vous devez annuler un épisode Live Room programmé, il est
          recommandé de contacter le Créateur pour l’informer de votre absence. Vous pouvez
          également demander à reprogrammer votre participation à un épisode futur. La communication
          est essentielle pour maintenir une bonne relation avec le Créateur et garantir une
          expérience optimale.
        </p>
        <h3>Demander un remboursement auprès du Créateur:</h3>
        <p>
          Si vous ne recevez pas ce qui a été promis par un Créateur, nous vous recommandons
          vivement de le contacter directement pour demander les avantages. Si, après cela, vous ne
          recevez toujours pas les avantages attendus, vous pouvez demander une annulation et un
          remboursement. Les remboursements sont disponibles dans les 48 heures suivant votre
          abonnement, à condition que vous n'ayez pas accédé à des avantages ou à du contenu réservé
          aux membres. Si vous avez attendu suffisamment longtemps mais n’avez toujours pas reçu ce
          qui était promis, vous pouvez contacter{' '}
          <a href="mailto:team@attractr.live">team@attractr.live</a> pour demander un remboursement.
          Gardez à l'esprit que les remboursements sont accordés à notre discrétion et que vous
          devez faire votre demande avant le 20 du mois au cours duquel vous avez acheté
          l'abonnement. Si le remboursement est approuvé, il sera traité en déduisant le montant du
          solde du Créateur.
        </p>
        <h3>Comment fonctionnent les remboursements sur Attractr:</h3>
        <p>
          Attractr ne traite pas les remboursements au nom des Créateurs, mais nous aidons à
          résoudre les problèmes et à faciliter les remboursements en déduisant le montant remboursé
          du solde du Créateur. Les Créateurs reçoivent leurs paiements le 20 de chaque mois, c'est
          pourquoi toutes les demandes de remboursement doivent être effectuées avant le 20 du même
          mois où vous vous êtes abonné. Si vous ne demandez pas de remboursement avant cette date,
          nous ne pourrons peut-être pas traiter votre demande, car les fonds sont transférés au
          Créateur.
        </p>
        <h3>Puis-je demander un remboursement pour un don ?</h3>
        <p>
          Non, les dons ne sont pas remboursables. Lorsque vous faites un don à un Créateur via la
          fonctionnalité de don, il est considéré comme une contribution unique pour soutenir ses
          efforts créatifs, et les remboursements ne s’appliquent pas.
        </p>
      </div>
    ),
  },
  {
    key: 'receipts',
    label: 'Reçus',
    topic: PAYMENTS_AND_REFUNDS,
    Html: (
      <div>
        <p>
          Besoin de reçus pour votre comptabilité ? Vous pouvez les télécharger et les imprimer
          directement depuis votre compte Attractr.
        </p>
        <h3>Accéder à votre historique de facturation:</h3>
        <ul>
          <li>
            Connectez-vous à votre compte Attractr et cliquez sur Historique des commandes dans le
            menu supérieur.
          </li>
          <li>
            Votre historique affichera la date de traitement, le montant facturé et le type d'achat
            (abonnement ou don).
          </li>
          <li>Vous recevrez également un message d'Attractr avec un lien vers votre reçu.</li>
        </ul>
        <h3>Télécharger vos reçus:</h3>
        <ol>
          <li>Accédez à Historique des commandes.</li>
          <li>Cliquez sur la transaction dont vous souhaitez télécharger le reçu.</li>
        </ol>
        <p>
          <strong>Remarque:</strong> Des conversions de devises peuvent apparaître sur votre relevé
          bancaire si votre devise locale diffère de celle de facturation du Créateur.
        </p>
      </div>
    ),
  },
  {
    key: 'taxes',
    label: 'Taxes',
    topic: PAYMENTS_AND_REFUNDS,
    Html: (
      <div>
        <p>
          Attractr est basé au Canada et opère à l’échelle mondiale. Les obligations fiscales sont
          déterminées en fonction de la localisation de l’acheteur (membres du public). Voici un
          aperçu des taxes qu’Attractr peut collecter :
        </p>
        <h3>Taxes de vente canadiennes (TPS/TVH/TVQ/TVP)</h3>
        <ul>
          <li>
            Acheteurs canadiens: Attractr perçoit la TPS/TVH, la TVQ ou la TVP applicable selon la
            province de l’acheteur. Par exemple, les acheteurs en Ontario paient la TVH au taux de
            l’Ontario, tandis que les acheteurs au Québec paient la TPS et la TVQ.{' '}
            <i>
              (*La taxe est calculée en fonction de l’adresse IP utilisée lors de la transaction.)
            </i>
          </li>

          <li>
            Acheteurs internationaux: Si l’acheteur est situé hors du Canada et que la transaction a
            lieu à l’extérieur du pays, aucune taxe canadienne ne s’applique.
          </li>
        </ul>
        <h3>Taux de taxe</h3>
        <ul>
          <li>TPS: 5 % (applicable à l’échelle nationale)</li>
          <li>TVH: Variable selon la province (ex.: 13 % en Ontario, 15 % en Nouvelle-Écosse)</li>
          <li>TVQ: 9,975 % (applicable au Québec)</li>
        </ul>
        <p>
          <strong>Remarque:</strong> Le montant total des taxes applicables est affiché lors du
          paiement.
        </p>
      </div>
    ),
  },
  {
    key: 'creating-a-show-page',
    label: 'Créer une page de spectacle',
    topic: BECOMING_A_CREATOR,
    Html: (
      <div>
        <p>
          Bienvenue sur Attractr ! Suivez ces étapes pour configurer votre Page de Spectacle, un hub
          dynamique qui centralise vos efforts de marketing, collabore avec les artistes et l’équipe
          technique, connecte avec le public et stimule les ventes de billets.
        </p>
        <h3>Qu’est-ce qu’une Page de Spectacle ?</h3>
        <p>
          Une Page de Spectacle est bien plus qu’une simple liste d’événements: c’est une plateforme
          interactive où vous pouvez présenter votre équipe, y compris les artistes, techniciens, et
          plus encore. Grâce aux Live Rooms, vous pouvez partager des contenus exclusifs des
          coulisses, des vidéos de fabrication, et recueillir des retours précieux du public. En
          créant un espace engageant, vous invitez le public à explorer et à se connecter
          émotionnellement à votre projet. Lorsque le public s’identifie à vos créateurs, il est
          plus susceptible de soutenir votre spectacle et d’y assister en personne. Une Page de
          Spectacle est essentielle pour rationaliser vos efforts marketing et favoriser
          l’engagement communautaire.
        </p>
        <p>
          De plus, le public et les créateurs peuvent laisser des avis et des notes sur les Pages de
          Spectacles, créant ainsi un référentiel précieux de connaissances et de retours tout au
          long de la durée de vie du spectacle. Une fois qu’une Page de Spectacle est créée, il
          n’est pas nécessaire d’en construire une nouvelle lors des tournées ou des représentations
          dans différents lieux: elle évolue et grandit avec le temps.
        </p>
        <h3>Avant de commencer:</h3>
        <p>
          Pour une configuration fluide, rassemblez les éléments suivants avant de créer votre Page
          de Spectacle:
        </p>
        <ul>
          <li>Image d'affiche: 1080 x 1920 pixels (ratio 9:16)</li>
          <li>Clips courts: 1080 x 1920 pixels (ratio 9:16), jusqu’à 30 secondes, 5 Mo</li>
          <li>
            Idées de Live Room: Collaborateurs, contenus des coulisses et avantages (codes promo,
            réductions, invitations spéciales).
          </li>
        </ul>
        <h3>Créer un Profil d’Organisation</h3>
        <p>Configurez votre Profil d’Organisation:</p>
        <ul>
          <li>
            Après votre inscription en tant que Créateur, vous aurez accès au Tableau de Bord
            Créateur. Si vous créez une Page de Spectacle pour une compagnie de production, accédez
            à Paramètres > Profils d’Organisation, puis cliquez sur +Ajouter un Profil
            d’Organisation ou modifiez "Nouveau projet" pour créer le profil de votre compagnie.
          </li>
          <li>
            Si le Profil d’Organisation existe déjà, demandez à votre équipe de vous ajouter en tant
            qu’Administrateur d’Équipe afin que vous puissiez gérer et créer des pages pour votre
            compagnie de production.
          </li>
        </ul>
        <h3>Spectacle à tout stade:</h3>
        <p>
          Peu importe le stade où se trouve votre spectacle — qu’il soit en développement ou en
          tournée active — vous pouvez le soumettre sur Attractr. Choisissez le statut actuel de
          votre spectacle:
        </p>
        <ul>
          <li>En tournée</li>
          <li>En théâtre</li>
          <li>Préventes</li>
          <li>En développement</li>
          <li>Recherche de fonds</li>
        </ul>
        <h3>Créer votre Page de Spectacle:</h3>
        <p>
          Créer une Page de Spectacle est simple ! Cliquez sur +Créer des Pages et sélectionnez
          Créer une Page de Spectacle.
        </p>
        <ul>
          <li>
            Détails: Entrez les informations nécessaires sur votre spectacle et sélectionnez votre
            Profil d’Organisation (Compagnie de Production). Si vous n’avez pas encore créé de
            Profil d’Organisation, sélectionnez +Créer un Profil d’Organisation.
          </li>
          <li>
            Contenu: Mettez en valeur votre spectacle avec des photos et des vidéos. Pour définir
            une miniature, cliquez sur les trois points de l’image téléchargée et choisissez Définir
            comme Affiche. Les vidéos doivent être des clips courts (jusqu’à 30 secondes, 5 Mo) pour
            offrir des avant-premières de votre spectacle. Vous pouvez ajouter de nouveaux clips à
            tout moment.
          </li>
          <li>
            Artistes & Équipe: Invitez vos artistes et membres de l’équipe ayant contribué au
            spectacle en cliquant sur Envoyer une Invitation. Ajoutez leur adresse e-mail et
            attribuez des crédits (titre et année). Pour les ajouter en tant qu’Équipe à votre
            organisation, cochez la case Ajouter en tant qu’Équipe de votre organisation.
          </li>
          <li>
            Billets: Ajoutez les informations sur vos billets et un lien vers la page de
            billetterie. Si les billets ne sont pas encore disponibles, cochez la case Non
            disponible sous statut de billetterie.
          </li>
        </ul>
        <p>
          Pas d’inquiétude ! Vous pouvez toujours mettre à jour et affiner votre Page de Spectacle
          après sa publication.
        </p>
        <h3>Examen et approbation:</h3>
        <p>
          Attractr examine chaque Page de Spectacle pour garantir qu’elle respecte les normes de
          qualité et contribue à une communauté dynamique et engageante. Une fois l’examen terminé,
          vous recevrez une notification, et votre Page de Spectacle sera publiée. Veuillez noter
          que votre Page de Spectacle ne sera pas mise en ligne tant que vous n’aurez pas créé au
          moins une Live Room associée.
        </p>
        <h3>Créer plusieurs Pages de Spectacle</h3>
        <p>
          Vous pouvez créer plusieurs Pages de Spectacle, des productions passées à celles à venir,
          et bâtir une belle communauté autour de vos spectacles.
        </p>
      </div>
    ),
  },
  {
    key: 'creating-a-live-room',
    label: 'Créer une Live Room',
    topic: BECOMING_A_CREATOR,
    Html: (
      <div>
        <p>
          Les contenus des coulisses et making-of offrent à votre public un aperçu exclusif de votre
          processus créatif, favorisant une connexion émotionnelle plus profonde avec votre projet.
          C’est plus qu’un simple aperçu: c’est une invitation à vous accompagner dans votre voyage.
          Ces moments des coulisses offrent non seulement des opportunités de monétisation, mais
          également un moyen de communication, de partage et d’engagement avec votre audience.
        </p>
        <h3>Qui peut créer une Live Room ?</h3>
        <p>
          Une Live Room est une page Créateur basée sur l’abonnement qui vous permet, à vous et
          votre équipe, de promouvoir collectivement votre spectacle à travers des perspectives
          personnelles. Les personnes suivantes peuvent créer une Live Room:
        </p>
        <ul>
          <li>Créateurs ayant une Page de Spectacle.</li>
          <li>Créateurs ajoutés en tant qu’équipe à l’Organisation.</li>
          <li>Administrateurs d’Équipe de l’Organisation.</li>
        </ul>
        <h3>Avant de commencer:</h3>
        <p>
          Nous vous recommandons de rassembler les éléments suivants avant de configurer votre Live
          Room sur Attractr:
        </p>
        <ul>
          <li>Photo de couverture (image 1024 x 768 pixels avec un ratio 4:3).</li>
          <li>
            Clips courts (jusqu’à 30 secondes, 5 Mo chacun) pour mettre en avant ou teaser votre
            projet.
          </li>
        </ul>
        <p>
          Les contenus des coulisses peuvent inclure tout ce qui donne à votre public un aperçu
          authentique et inédit de votre projet. C’est comme un documentaire en direct, capturant la
          façon dont votre projet prend vie à travers le regard unique de chaque Créateur. Les
          Créateurs peuvent inclure non seulement les artistes, mais aussi les techniciens,
          régisseurs, costumiers, musiciens, directeurs de scène, et bien d’autres.
        </p>
        <h3>Voici quelques exemples de contenus des coulisses appréciés par les audiences:</h3>
        <ul>
          <li>Entraînement quotidien au studio ou à la maison.</li>
          <li>Activités durant les jours de repos.</li>
          <li>Performances de rue ou flash mobs.</li>
          <li>Répétitions ou sessions de travail.</li>
          <li>Ateliers de brainstorming ou de concepts créatifs.</li>
          <li>Échauffements avant les représentations.</li>
          <li>Visites des coulisses.</li>
          <li>Construction de scènes ou sessions de scénographie.</li>
          <li>Discussions post-spectacle en loge ou au restaurant.</li>
          <li>Conseils sur les costumes et la beauté.</li>
          <li>Exploration de nouvelles villes en tournée.</li>
          <li>L’heure avant le lever de rideau.</li>
          <li>Les possibilités sont infinies !</li>
        </ul>
        <h3>Planifiez vos épisodes de Live Room:</h3>
        <p>
          Une Live Room est une expérience vidéo en ligne qui vous permet d’interagir avec votre
          public grâce à des abonnements par niveaux. Tout ce dont vous avez besoin est un appareil
          mobile ou un ordinateur pour diffuser en direct et partager des moments des coulisses en
          temps réel. Voici ce qu’il faut considérer avant de démarrer votre Live Room:
        </p>
        <ul>
          <li>De quoi traite votre Live Room ?</li>
          <li>
            Donnez un nom à votre Live Room (nous recommandons d’utiliser votre nom si vous n’avez
            pas de titre de marque).
          </li>
          <li>Quels avantages pouvez-vous offrir pour les abonnements ?</li>
          <li>À quelle fréquence pouvez-vous diffuser en direct des expériences des coulisses ?</li>
        </ul>
        <p>
          Votre Live Room sert de chaîne personnelle et peut rester active même après la fin du
          spectacle associé. Vous pouvez la lier à de nouveaux spectacles auxquels vous participez,
          maintenant ainsi la continuité avec votre public. Cependant, si vous êtes retiré en tant
          que membre de l’équipe de l’organisation, vous perdrez la propriété de la Live Room. Nous
          travaillons activement à améliorer les fonctionnalités de permission d’équipe pour
          garantir la continuité de la propriété pour les Créateurs. Restez à l’écoute pour les
          mises à jour !
        </p>
        <h3>Créez un abonnement:</h3>
        <p>
          Une fois que vous avez ajouté les détails et le branding de votre Live Room, il est temps
          de configurer vos niveaux d’abonnement. Vous pouvez créer autant de niveaux que vous le
          souhaitez, mais assurez-vous de pouvoir fournir les avantages promis à votre audience.
        </p>
        <ul>
          <li>
            Choisissez un titre pour le niveau d’abonnement (par exemple, Membre, Lite, Elite, Pro,
            Teaser, Early Birds, Super Fan, etc.).
          </li>
          <li>Ajoutez une description du niveau.</li>
          <li>Fixez un prix et une durée pour l’abonnement.</li>
          <li>Sélectionnez les avantages pour chaque niveau.</li>
        </ul>
        <h3>Types d’avantages:</h3>
        <ul>
          <li>Épisodes en direct réservés aux membres (par défaut).</li>
          <li>Codes promo pour les billets (envoyés par message).</li>
          <li>Réductions sur les produits dérivés (envoyées par message).</li>
          <li>Messages de remerciements.</li>
          <li>Accès anticipé aux billets (envoyé par message).</li>
          <li>Crédit dans le spectacle au lancement.</li>
          <li>Invitations à des événements spéciaux (envoyées par message).</li>
        </ul>
        <p>
          <strong>Remarque:</strong> Les épisodes en direct réservés aux membres sont l’avantage par
          défaut pour tous les abonnements Live Room. Chaque abonné recevra cet avantage, vous
          devrez donc planifier des créneaux horaires de Live Room réservés aux membres lors de la
          création des abonnements.
        </p>
        <h3>Paiement et durée des abonnements:</h3>
        <p>
          Les abonnements sont des paiements uniques, avec des durées définies par vous.
          L’abonnement est valide pour le nombre de jours ou de mois spécifié à partir de la date
          d’achat. Cela vous permet de créer des abonnements adaptés à différents parcours
          d’audience, tels que:
        </p>
        <ul>
          <li>Abonnements d’un mois pour les utilisateurs indécis à propos des billets.</li>
          <li>
            Abonnements de six mois pour les super fans qui souhaitent soutenir et interagir tout au
            long de votre projet.
          </li>
        </ul>
        <h3>Ajoutez des créneaux horaires d’événements Live Room:</h3>
        <p>
          Une fois vos niveaux d’abonnement configurés, vous pouvez commencer à programmer des
          épisodes de Live Room — gratuits et payants — pour au moins le mois à venir.
        </p>
        <ul>
          <li>Cliquez sur +Ajouter un créneau.</li>
          <li>
            Entrez le titre et sélectionnez le type d’événement Live Room (par exemple, Accès
            anticipé, BTS, Q&A, Café discussion).
          </li>
          <li>Définissez la date et l’heure (choisissez votre fuseau horaire).</li>
          <li>Pour répéter l’épisode, ajoutez plus de dates.</li>
          <li>
            Choisissez le type d’accès: Tout le monde (gratuit) ou Membres uniquement (abonnés
            payants).
          </li>
        </ul>
        <h3>Aperçu et sauvegarde:</h3>
        <p>
          Une fois votre Live Room configurée, cliquez sur Aperçu pour voir comment elle se
          présente. Lorsque tout est prêt, cliquez sur Sauvegarder pour publier.
        </p>
        <h3>Puis-je créer plusieurs Live Rooms ?</h3>
        <p>Oui, les créateurs peuvent créer et gérer plusieurs Live Rooms.</p>
      </div>
    ),
  },
  {
    key: 'delivering-perks-to-your-members',
    label: 'Fournir des avantages à vos membres',
    topic: BECOMING_A_CREATOR,
    Html: (
      <div>
        <p>
          Les Live Rooms offrent la possibilité de créer des avantages réservés aux membres. Ces
          avantages sont exclusivement disponibles pour vos membres actifs et payants, vos super
          fans qui sont là pour vous soutenir. En tant que Créateur, il est de votre responsabilité
          de fournir ces avantages et de maintenir l’engagement de vos membres.
        </p>
        <h3>Programmez régulièrement des épisodes Live Room réservés aux membres:</h3>
        <p>
          Vous pouvez planifier des épisodes Live Room réservés aux membres, accessibles uniquement
          à vos membres actifs. Bien que tout le monde puisse découvrir l’épisode, seuls les membres
          peuvent y assister en direct. Pour maintenir l’engagement, assurez-vous de planifier ces
          épisodes fréquemment ou périodiquement.
        </p>
        <ul>
          <li>Allez dans Calendrier.</li>
          <li>Cliquez sur Ajouter un créneau et sélectionnez une Live Room.</li>
          <li>Définissez le créneau pour Membres uniquement.</li>
          <li>
            Si l’événement est destiné à un membre payant spécifique, cliquez sur Inviter et tapez
            le nom du membre.
          </li>
        </ul>
        <h3>Fournissez des avantages via message:</h3>
        <p>
          Pour les avantages tels que les codes promo, les réductions sur les produits dérivés,
          l’accès anticipé aux billets ou les invitations à des événements spéciaux (en ligne ou
          hors ligne), vous pouvez les transmettre directement à vos membres via des messages.
        </p>
        <ul>
          <li>Allez dans Audience > Membres.</li>
          <li>Sélectionnez un membre payant et cliquez sur Message.</li>
          <li>Envoyez directement les informations sur l’avantage au membre.</li>
        </ul>
        <h3>Utilisez des réponses rapides:</h3>
        <p>
          Pour simplifier le processus, vous pouvez utiliser les Réponses Rapides pour créer des
          modèles de messages afin de fournir des avantages personnalisés ou des messages à vos
          membres.
        </p>
        <ul>
          <li>Allez dans Boîte de réception.</li>
          <li>Cliquez sur +Nouvelle réponse.</li>
          <li>
            Ajoutez un titre au message (par exemple, "Merci de vous abonner à ma Live Room !").
          </li>
          <li>Créez un modèle de message et cliquez sur Soumettre.</li>
          <li>
            Dans chaque fil de message, cliquez sur Réponse Rapide et sélectionnez le modèle que
            vous souhaitez utiliser.
          </li>
        </ul>
      </div>
    ),
  },
  {
    key: 'managing-your-live-room-episodes',
    label: 'Gérer vos épisodes de Live Room',
    topic: BECOMING_A_CREATOR,
    Html: (
      <div>
        <p>
          Étant donné que la Live Room est une expérience de chat vidéo en direct en ligne, tous les
          épisodes de Live Room, qu’ils soient gratuits ou payants, sont programmés pour être
          diffusés en direct à des heures spécifiques. Bien que construire une audience puisse
          prendre du temps, publier régulièrement est l’un des meilleurs moyens de développer votre
          communauté et d’augmenter vos revenus. Publier régulièrement peut signifier
          quotidiennement, hebdomadairement ou mensuellement, selon ce qui fonctionne le mieux pour
          vous, votre contenu et vos abonnés. L’utilisation d’un calendrier de contenu aligné sur
          votre planning de projet vous aidera à planifier et organiser efficacement vos épisodes de
          Live Room.
        </p>
        <h3>Comment programmer des événements Live Room:</h3>
        <ul>
          <li>Accédez au Calendrier.</li>
          <li>Cliquez sur Ajouter un créneau et sélectionnez votre Live Room.</li>
          <li>Cliquez sur +Ajouter un créneau.</li>
          <li>
            Remplissez le titre et choisissez le type d’événement (par exemple, Accès anticipé, BTS,
            Questions/Réponses, Café Discussion, etc.).
          </li>
          <li>Définissez la date, l’heure et la durée.</li>
          <li>Pour répéter l’épisode, ajoutez des dates supplémentaires.</li>
          <li>
            Choisissez le type d’accès: Tout le monde (gratuit) ou Membres uniquement (abonnés
            payants).
          </li>
          <li>Cliquez sur Ajouter.</li>
        </ul>
        <h3>Comment annuler un événement Live Room:</h3>
        <ul>
          <li>Accédez au Gestionnaire de pages et sélectionnez la Live Room.</li>
          <li>Cliquez sur Modifier la liste.</li>
          <li>Accédez aux Plannings.</li>
          <li>
            Cliquez sur l’icône de suppression à côté du créneau horaire que vous souhaitez annuler.
          </li>
          <li>Les membres ayant déjà réservé l’événement recevront une notification.</li>
        </ul>
        <h3>Comment modifier vos créneaux horaires de Live Room:</h3>
        <ul>
          <li>Accédez au Calendrier.</li>
          <li>Sélectionnez l’événement Live Room que vous souhaitez modifier.</li>
          <li>
            Apportez les modifications nécessaires (par exemple, titre, date, heure ou type
            d’accès).
          </li>
          <li>Cliquez sur Enregistrer pour mettre à jour.</li>
        </ul>
      </div>
    ),
  },
  {
    key: 'how-to-start-a-live-room-episode',
    label: 'Comment démarrer un épisode de Live Room',
    topic: BECOMING_A_CREATOR,
    Html: (
      <div>
        <p>
          Lorsque vous créez un événement Live Room en ajoutant des créneaux horaires, chaque
          événement génère automatiquement un lien vidéo, qui est partagé avec les spectateurs ayant
          réservé l’événement.
        </p>
        <h3>À propos des événements Live Room</h3>
        <p>
          Attractr utilise Whereby, un outil de visioconférence basé sur le navigateur qui offre une
          vidéo de haute qualité, sécurisée et facile à utiliser. Chaque événement Live Room peut
          actuellement accueillir jusqu’à 100 participants.
        </p>
        <h3>Ce dont vous avez besoin avant de commencer:</h3>
        <p>
          Contrairement à des plateformes comme YouTube ou Twitch, qui nécessitent des
          configurations de studio, des caméras haut de gamme et un équipement audio/visuel
          complexe, Attractr se concentre sur la création de communauté grâce à des expériences
          vidéo en direct simples en ligne. Pour commencer, vous avez seulement besoin de:
        </p>
        <ul>
          <li>Une connexion Wi-Fi fiable</li>
          <li>
            Un appareil mobile/tablette ou un ordinateur de bureau équipé d’une caméra intégrée
          </li>
        </ul>
        <p>
          Bien sûr, optimiser votre configuration pour une meilleure qualité vidéo peut être utile à
          mesure que votre communauté grandit, mais ce n’est pas nécessaire pour débuter.
        </p>
        <h3>Changer la source d’entrée vidéo ou audio (ordinateur uniquement):</h3>
        <ul>
          <li>Si vous préférez modifier votre source d’entrée vidéo ou audio sur un ordinateur:</li>
          <li>Accédez aux Paramètres.</li>
          <li>Allez dans Appareils > Caméra ou Audio.</li>
          <li>Dans la liste déroulante, sélectionnez votre appareil préféré.</li>
          <li>Cliquez sur Enregistrer.</li>
        </ul>
        <h3>Démarrer votre événement Live Room:</h3>
        <ul>
          <li>
            Accédez au Calendrier et sélectionnez le créneau horaire de votre événement à venir.
          </li>
          <li>Vous y trouverez le lien vidéo.</li>
          <li>
            Cliquez sur le lien pour démarrer votre événement Live Room et accueillir votre public.
          </li>
        </ul>
        <h3>Ce que vous pouvez faire dans une Live Room:</h3>
        <ul>
          <li>Discuter en direct avec les participants</li>
          <li>Diffuser en direct sur YouTube/Vimeo</li>
          <li>Partager votre écran pour montrer du contenu supplémentaire à votre audience</li>
        </ul>
      </div>
    ),
  },
  {
    key: 'messaging-members',
    label: 'Envoyer des messages aux membres',
    topic: BECOMING_A_CREATOR,
    Html: (
      <div>
        <p>
          Les messages personnalisés des créateurs sont un immense avantage pour les audiences et
          les membres. Lorsqu'une personne réserve ou s'abonne à votre Live Room, un fil de messages
          est automatiquement généré entre vous et le membre de l'audience. Vous pouvez ensuite leur
          envoyer des messages directs.
        </p>
        <h3>Comment envoyer un message à votre membre:</h3>
        <ul>
          <li>Accédez à la Messagerie.</li>
          <li>
            Sélectionnez la personne à qui vous souhaitez envoyer un message et commencez la
            conversation.
          </li>
        </ul>
        <h3>Qu'est-ce que le point vert ?</h3>
        <p>
          Le point vert indique quand vous ou les membres de votre audience êtes disponibles en
          ligne. Vous pouvez voir lesquels de vos contacts sont disponibles pour recevoir des
          messages en temps réel en recherchant le point vert à côté de leur photo de profil dans la
          messagerie.
        </p>
        <h3>Utilisez les Réponses Rapides:</h3>
        <p>
          Pour gagner du temps, vous pouvez utiliser les Réponses Rapides pour envoyer des messages
          personnalisés ou délivrer des avantages aux membres payants.
        </p>
        <ul>
          <li>Accédez à la Messagerie.</li>
          <li>Cliquez sur +Nouvelle réponse.</li>
          <li>
            Ajoutez un titre de message (par ex.: "Merci de vous être abonné à ma Live Room !").
          </li>
          <li>Créez un modèle de message et cliquez sur Soumettre.</li>
          <li>
            Dans tout fil de messages, cliquez sur Réponse Rapide et choisissez le modèle que vous
            souhaitez utiliser.
          </li>
        </ul>
        <h3>Comment créer un chat de groupe pour les participants d'un épisode Live Room:</h3>
        <p>
          Vous pouvez facilement créer un chat de groupe pour les participants d'un événement Live
          Room spécifique, leur permettant d’interagir entre eux et avec vous.
        </p>
      </div>
    ),
  },
  {
    key: 'kickstarting-a-community-on-attractr',
    label: 'Lancer une communauté sur Attractr',
    topic: GROW_YOUR_COMMUNITY,
    Html: (
      <div>
        <p>
          Attractr est un outil puissant pour les compagnies de production qui souhaitent bâtir une
          audience engagée autour de leurs spectacles en collaborant avec des artistes et des
          créateurs. Ce guide fournit les étapes essentielles pour vous aider à construire une
          communauté florissante qui soutient vos spectacles actuels et favorise un engagement à
          long terme.
        </p>
        <h3>Étape 1: Créez votre profil d'organisation</h3>
        <p>
          Commencez par configurer un profil d'organisation qui représente clairement l'identité de
          votre compagnie de production. Cela inclut:
        </p>
        <ul>
          <li>Logo: Un logo reconnaissable qui renforce votre marque.</li>
          <li>Bio: Une biographie captivante qui raconte votre histoire.</li>
          <li>
            Détails de l'organisation: Fournissez des informations essentielles qui renforcent la
            crédibilité de votre profil.
          </li>
        </ul>
        <p>
          Votre profil d'organisation établit la première impression pour votre communauté et crée
          une base professionnelle pour un développement ultérieur.
        </p>
        <h3>Étape 2: Créez des pages de spectacles</h3>
        <p>
          Ensuite, développez votre catalogue de productions en créant des pages de spectacles pour
          chacun de vos projets:
        </p>
        <ul>
          <li>
            Complétez les détails: Fournissez toutes les informations nécessaires sur le spectacle
            pour offrir au public une vue d'ensemble complète.
          </li>
          <li>
            Invitez les artistes et l'équipe: Encouragez votre équipe à rejoindre la page, ce qui
            ajoute de la légitimité au spectacle et renforce la promotion collaborative.
          </li>
          <li>
            Branding visuel: Ajoutez une vignette d'affiche et des vidéos d'aperçu pour mettre en
            valeur le spectacle et rendre la page plus attrayante pour les visiteurs.
          </li>
        </ul>
        <p>
          Chaque page de spectacle agit comme le hub de votre production, vous permettant de
          promouvoir le projet, de bâtir une audience et de vous connecter avec les artistes et
          l'équipe.
        </p>
        <h3>Étape 3: Créez des Live Rooms</h3>
        <p>
          Utilisez les Live Rooms pour interagir de manière significative avec votre audience.
          Commencez par créer une Live Room au nom de votre compagnie de production, puis invitez
          les artistes et l’équipe à rejoindre l’Organisation en tant qu’Équipe pour animer leurs
          propres Live Rooms sous la Page de Spectacle associée:
        </p>
        <ul>
          <li>
            Soyez authentique: Planifiez régulièrement des épisodes de Live Room qui montrent les
            coulisses, des répétitions aux activités quotidiennes. Les épisodes peuvent durer de 30
            minutes à 5 heures, selon le contenu.
          </li>
          <li>
            Engagez votre audience: Partager des moments réels et non scénarisés aide à créer une
            connexion plus profonde avec votre audience et les maintient investis dans votre
            production. Les Live Rooms offrent l'espace parfait pour une interaction en temps réel.
          </li>
        </ul>
        <h3>Étape 4: Définissez des avantages uniques</h3>
        <p>
          Pour vraiment engager votre audience, offrez des avantages uniques qui correspondent à
          leurs intérêts:
        </p>
        <ul>
          <li>Contenu exclusif: Coulisses, accès anticipé au contenu ou aperçus exclusifs.</li>
          <li>
            Expériences personnalisées: Dédicaces, sessions de questions-réponses ou visites
            virtuelles.
          </li>
        </ul>
        <p>
          Adaptez les avantages en fonction de ce qui résonne le plus avec votre audience et
          offrez-les via des niveaux d'abonnement ou des épisodes de Live Room pour inciter à la
          participation.
        </p>
        <h3>Étape 5: Trouvez votre première audience</h3>
        <p>Commencez votre communauté en contactant vos premiers soutiens:</p>
        <ul>
          <li>
            Envoyez un e-mail à vos abonnés de newsletter: Invitez-les à rejoindre Attractr et à
            soutenir vos projets.
          </li>
          <li>
            Faites la promotion sur les réseaux sociaux: Partagez des mises à jour, des liens vers
            les Live Rooms et des contenus en coulisses pour attirer vos abonnés existants.
          </li>
          <li>
            Réseautez sur Attractr: Rejoignez les communautés d'autres créateurs, soutenez leur
            travail et invitez-les à vos propres Live Rooms.
          </li>
          <li>
            Rejoignez le Forum Communautaire Attractr: Engagez-vous avec d'autres membres, partagez
            des idées et collaborez avec des créateurs pour élargir votre portée.
          </li>
        </ul>
        <p>
          Votre première audience est essentielle pour une croissance par le bouche-à-oreille, alors
          cultivez des relations significatives et encouragez-les à partager l'information.
        </p>
        <h3>Étape 6: Continuez à grandir</h3>
        <p>
          La cohérence est essentielle pour maintenir la croissance de votre communauté. Continuez à
          organiser régulièrement des épisodes de Live Room, partagez du contenu exclusif et
          interagissez avec vos membres. À mesure que votre communauté grandit, vos ventes de
          billets, votre influence et la fidélité de vos fans augmenteront également. Construire une
          communauté passionnée prend du temps, mais avec un engagement continu et de la dévotion,
          votre audience s'élargira et prospérera.
        </p>
      </div>
    ),
  },
  {
    key: 'how-to-grow-your-members',
    label: 'Comment développer vos membres',
    topic: GROW_YOUR_COMMUNITY,
    Html: (
      <div>
        <p>
          Attractr est conçu pour aider les compagnies de production à exploiter le potentiel des
          histoires inédites en coulisses, favorisant ainsi la croissance de leurs spectacles à
          n'importe quelle étape. En collaborant avec des artistes et des créateurs, les compagnies
          de production peuvent rationaliser leurs efforts marketing tout en captivant le public de
          manière nouvelle et excitante.
        </p>
        <h3>Promotion collaborative avec le casting et l'équipe</h3>
        <p>
          En attribuant votre casting et votre équipe en tant que membres de l’Équipe, ils peuvent
          associer leurs Live Rooms au spectacle auquel ils participent et le promouvoir
          collectivement. Les Live Rooms liées à une Page de Spectacle seront regroupées sous cette
          page, permettant aux administrateurs de l’organisation d’accéder à tous les membres de ces
          Live Rooms via Insights > Membres. Cette base de données offre des opportunités précieuses
          pour engager les fans, renforcer leur fidélité, attirer du public dans les théâtres et
          mieux comprendre les préférences de votre audience.
        </p>
        <h3>Comment développer votre base de membres:</h3>
        <p>
          Il n’existe pas de méthode unique pour développer votre base d’abonnés, mais voici
          quelques bonnes pratiques pour tirer parti des Live Rooms et du contenu en coulisses:
        </p>
        <ul>
          <li>
            Complétez votre casting et votre équipe: Assurez-vous d’attribuer des crédits à tous les
            contributeurs sur votre page de spectacle, améliorant ainsi leur visibilité.
          </li>
          <li>
            Autonomisez votre casting et votre équipe: Permettez à votre casting et à votre équipe
            d'animer des Live Rooms pendant la production, comme des visites des coulisses, des
            répétitions ou d'autres moments exclusifs en coulisses.
          </li>
          <li>
            Offrez des avantages: Proposez des incitations pour que les créateurs conçoivent des
            abonnements qui encouragent la vente de billets et l'engagement du public.
          </li>
          <li>
            Ajoutez des administrateurs d'équipe: Collaborez en ajoutant des administrateurs
            d'équipe qui peuvent aider à gérer les créateurs et les Live Rooms, garantissant une
            exécution fluide et une meilleure interaction avec les membres.
          </li>
        </ul>
        <h3>Transformer les coulisses en une activité médiatique:</h3>
        <p>
          Permettre à votre casting et à votre équipe d’animer des Live Rooms signifie leur donner
          accès à la capture des moments en coulisses qui donnent vie à la production pour votre
          audience. Comme Attractr prend actuellement en charge uniquement le contenu en direct, il
          est essentiel d'encourager le public à réserver des créneaux horaires spécifiques pour
          participer à ces expériences exclusives. En transformant le contenu en coulisses en médias
          engageants, vous créez une nouvelle source de revenus et d’engagement des fans.
        </p>
      </div>
    ),
  },
  {
    key: 'turning-your-behind-the-scenes-to-media-business',
    label: 'Transformer vos coulisses en entreprise médiatique',
    topic: GROW_YOUR_COMMUNITY,
    Html: (
      <div>
        <p>
          Le contenu en coulisses offre aux compagnies de production un moyen puissant de
          transformer des moments quotidiens en expériences médiatiques engageantes et rentables. En
          mettant en lumière le processus brut et authentique derrière vos productions, vous pouvez
          créer des récits captivants qui captivent votre public et génèrent de nouvelles sources de
          revenus. Voici comment transformer le contenu en coulisses en une activité médiatique
          durable:
        </p>
        <h3>Étape 1: Développez une stratégie</h3>
        <ul>
          <li>
            Définissez un objectif: Fixez des objectifs clairs pour votre contenu en coulisses,
            qu'il s'agisse d'élargir votre audience, d'augmenter l'engagement ou de stimuler la
            vente de billets.
          </li>
          <li>
            Identifiez des histoires inédites: Exploitez des ressources inutilisées comme les
            moments en coulisses, les répétitions ou les routines quotidiennes qui mettent en avant
            l'essence de votre production.
          </li>
          <li>
            Créez un système: Désignez des administrateurs d’équipe pour intégrer Attractr dans
            votre flux de travail quotidien, permettre aux artistes et créateurs d’animer des
            épisodes de Live Room et gérer la livraison des avantages.
          </li>
          <li>
            Définissez un calendrier de Live Room: Établissez un planning cohérent pour votre
            contenu afin de maintenir l’intérêt de votre audience et l’inciter à anticiper vos
            prochaines mises à jour.
          </li>
        </ul>
        <h3>Étape 2: Donnez du pouvoir à vos artistes et votre équipe</h3>
        <p>
          Votre casting et votre équipe possèdent des talents et des personnalités uniques que le
          public ne voit souvent jamais. En leur donnant la plateforme pour animer leurs propres
          épisodes de Live Room, vous leur permettez de développer leur marque personnelle et de
          créer de nouvelles sources de revenus. Mettre en lumière leurs histoires inédites enrichit
          non seulement la profondeur du spectacle, mais leur permet également de s'engager avec les
          fans d'une manière nouvelle, ajoutant du sens et de la valeur à leur passion.
        </p>
        <h3>Étape 3: Préparez le terrain pour les paiements</h3>
        <ul>
          <li>
            Ajoutez des comptes de paiement: Configurez des comptes de paiement pour chaque Live
            Room, garantissant que les revenus des abonnements vont directement à l’hôte de la Live
            Room.
          </li>
          <li>
            Fixez les prix des abonnements de manière stratégique: Basez les prix sur la valeur
            unique que vous offrez à travers le contenu en coulisses et les avantages, tels que
            l’accès anticipé, des aperçus exclusifs ou des remerciements personnalisés.
          </li>
        </ul>
        <h3>Étape 4: Commencez petit avec vos réseaux proches</h3>
        <ul>
          <li>
            Testez vos Live Rooms: Commencez avec votre audience initiale et ajustez votre approche
            en fonction de leurs retours.
          </li>
          <li>
            Invitez le casting et l’équipe: Encouragez-les à participer à la promotion des
            spectacles et à développer leurs réseaux professionnels sur Attractr. Cela renforce
            également leur rôle d'ambassadeurs de votre production.
          </li>
        </ul>
        <h3>Étape 5: Convertissez les membres gratuits en abonnés payants</h3>
        <ul>
          <li>
            Organisez des épisodes gratuits de Live Room: Utilisez du contenu gratuit comme point
            d'entrée pour susciter l'intérêt et l'engagement. Cela donne un aperçu de ce qui est à
            venir et peut inciter à la conversion en abonnements payants.
          </li>
          <li>
            Envoyez des messages personnalisés: Renforcez les relations grâce à des communications
            directes et des interactions personnalisées pour fidéliser les membres gratuits et en
            faire des abonnés payants.
          </li>
        </ul>
      </div>
    ),
  },
  {
    key: 'promote-a-show-while-making-money',
    label: "Promouvoir un spectacle tout en gagnant de l'argent",
    topic: GROW_YOUR_COMMUNITY,
    Html: (
      <div>
        <p>
          Imaginez un théâtre rempli avec de nombreux visages familiers, tous ayant soutenu votre
          parcours à travers le Live Room. Pour les producteurs, les marketeurs et les agents,
          attirer les meilleurs talents, les garder motivés et réussir ensemble est un véritable
          défi. Sans créateurs, il n’y a pas de spectacle. Avec Attractr, les compagnies de
          production peuvent désormais promouvoir leurs spectacles collectivement tout en générant
          des revenus supplémentaires.
        </p>
        <h3>Comment Attractr vous aide à gagner de l'argent tout en promouvant votre spectacle:</h3>
        <p>
          Attractr permet aux créateurs de mettre en valeur leurs talents à la fois sur scène et en
          dehors. En utilisant les Live Rooms, les créateurs peuvent transformer du contenu en
          coulisses en récits captivants, favoriser les relations avec le public et bâtir une
          communauté dévouée. Cela ouvre également la porte à de nouvelles sources de revenus en
          permettant aux créateurs de monétiser du contenu exclusif, de développer des abonnements
          et de collecter des fonds.
        </p>
        <h3>1. Proposez du contenu gratuit et payant:</h3>
        <p>
          Utilisez des épisodes gratuits de Live Room comme une introduction à votre communauté, en
          donnant au public un aperçu de votre monde en coulisses. Ensuite, encouragez-les à
          rejoindre vos abonnements payants pour accéder à du contenu exclusif, des avant-premières
          ou des interactions spéciales en direct avec le casting et l’équipe.
        </p>
        <h3>2. Créez des abonnements à plusieurs niveaux:</h3>
        <p>Offrez différents niveaux d’abonnement avec divers niveaux d’accès. Par exemple:</p>
        <ul>
          <li>
            Forfait de base: Donne accès à des mises à jour en coulisses et des avant-premières.
          </li>
          <li>
            Forfait premium: Inclut des interactions en direct, un accès anticipé aux billets et des
            remerciements personnalisés.
          </li>
        </ul>
        <p>
          En diversifiant les options d’abonnement, vous pouvez répondre à un éventail plus large
          d’intérêts du public et augmenter vos sources de revenus.
        </p>
        <h3>3. Engagez et récompensez votre communauté:</h3>
        <p>
          Plus vous interagissez avec votre public, plus il s’investit. Organisez régulièrement des
          sessions de questions-réponses en direct, des visites en coulisses ou des répétitions
          exclusives pour les garder engagés. De plus, récompensez vos membres fidèles avec des
          avantages personnalisés, comme des notes de remerciement ou un accès anticipé aux billets.
        </p>
        <h3>4. Exploitez la collaboration:</h3>
        <p>
          Invitez votre casting et votre équipe à participer aux Live Rooms et à interagir avec le
          public. Leurs perspectives et personnalités uniques aident à élargir votre portée et à
          attirer de nouveaux abonnés au spectacle, tout en promouvant collectivement votre
          production.
        </p>
        <h3>5. Faites la promotion auprès de votre réseau:</h3>
        <p>
          Partagez votre Live Room et du contenu exclusif sur vos réseaux sociaux, vos newsletters
          et au sein de la communauté Attractr. Invitez vos supporters, fans et abonnés actuels à
          vous rejoindre sur Attractr et à découvrir le spectacle d'une nouvelle manière immersive.
          Cela crée un effet boule de neige, encourageant davantage de personnes à participer et
          potentiellement à investir dans votre spectacle.
        </p>
        <p>
          Avec Attractr, votre spectacle devient bien plus qu’une expérience sur scène—il se
          transforme en une activité médiatique durable. En combinant contenu en coulisses et
          engagement du public, vous pouvez promouvoir votre spectacle tout en générant des revenus
          supplémentaires, et ce, tout en renforçant votre communauté.
        </p>
      </div>
    ),
  },
  {
    key: 'pages',
    label: 'Pages',
    topic: CREATOR_DASHBOARD,
    Html: (
      <div>
        <p>
          Le Creator Dashboard est votre outil central pour gérer et développer votre activité sur
          Attractr. Il permet aux créateurs de gérer leurs Production Pages et Live Rooms,
          collectivement appelées Pages. Vous pouvez créer autant de Pages que vous le souhaitez, à
          condition de pouvoir les gérer efficacement.
        </p>
        <h3>Publier une Production Page:</h3>
        <p>
          Pour publier une Production Page, cliquez sur + Créer des Pages et renseignez tous les
          détails requis. Une fois terminé, Attractr examinera votre page pour s'assurer qu'elle
          respecte les normes de qualité, afin de maintenir une communauté dynamique. Pendant le
          processus de révision, le statut de votre Production Page apparaîtra comme En cours de
          révision dans la colonne de statut. Après approbation, vous recevrez une notification, et
          votre Production Page sera publiée. Notez que votre Production Page ne sera pas en ligne
          tant que vous n'aurez pas créé et lié au moins un Live Room à celle-ci.
        </p>
        <h3>Modifier vos Pages après publication:</h3>
        <p>
          Contrairement à d'autres plateformes, vous n'avez pas besoin de créer une nouvelle page
          pour chaque mise à jour ou tournée en ville. Mettez simplement à jour vos informations à
          mesure que votre projet évolue, en conservant votre communauté en cours de route.
        </p>
        <ul>
          <li>Allez dans Pages.</li>
          <li>Sélectionnez la page que vous souhaitez modifier et cliquez sur Modifier la Page.</li>
        </ul>
        <h3>Puis-je supprimer ma Page ?</h3>
        <p>
          Vous ne pouvez pas supprimer complètement une Page, mais vous pouvez fermer l'annonce pour
          la retirer d'Attractr.
        </p>
        <ul>
          <li>Allez dans Pages.</li>
          <li>Sélectionnez l'annonce et cliquez sur Fermer la Page.</li>
        </ul>
        <h3>Puis-je réactiver ma Page ?</h3>
        <p>Oui, vous pouvez facilement rouvrir une annonce fermée.</p>
        <ul>
          <li>Allez dans Pages.</li>
          <li>Sélectionnez l'annonce et cliquez sur Ouvrir la Page.</li>
        </ul>
        <p>Votre Page sera de nouveau publiée.</p>
        <h3>Les membres de l'Équipe peuvent-ils gérer les Pages des Créateurs?</h3>
        <p>
          Oui, un membre de l'Équipe peut aider à gérer et soutenir les Pages des Créateurs. Toutes
          les Live Rooms et Pages associées au Profil de l’Organisation sont centralisées dans la
          vue du Gestionnaire d’Organisation (la personne qui a créé le Profil de l’Organisation).
          Le Gestionnaire d’Organisation peut inviter des membres de l'Équipe, leur donnant accès à
          la gestion des Pages, du Calendrier, de la Messagerie et des Statistiques, garantissant
          une coordination fluide et une gestion efficace du contenu des Créateurs.
        </p>
      </div>
    ),
  },
  {
    key: 'calendar',
    label: 'Calendrier',
    topic: CREATOR_DASHBOARD,
    Html: (
      <div>
        <p>
          Programmer vos épisodes de Live Room est crucial pour maintenir l'engagement de votre
          audience. Nous vous recommandons de définir des jours et des heures réguliers afin que les
          membres sachent quand se connecter. Vous pouvez facilement mettre à jour votre planning ou
          ajouter des épisodes spécifiques à l'aide de la fonctionnalité Calendrier pour garder tout
          à jour.
        </p>
        <h3>Comment programmer des événements Live Room:</h3>
        <ul>
          <li>Allez dans Calendrier.</li>
          <li>Cliquez sur Ajouter de nouveaux épisodes et sélectionnez votre Live Room.</li>
          <li>Cliquez sur +Ajouter un nouvel épisode sur la page de création de Live Room.</li>
          <li>Remplissez les détails de l'événement: titre, type, date, heure et durée.</li>
          <li>Vous pouvez dupliquer l'épisode en ajoutant des dates supplémentaires.</li>
          <li>Choisissez l'accès: Gratuit ou réservé aux membres.</li>
          <li>Cliquez sur Ajouter.</li>
        </ul>
        <h3>Détails des épisodes Live Room:</h3>
        <ul>
          <li>
            <strong>Titre:</strong> Choisissez un titre descriptif et engageant.
          </li>
          <li>
            <strong>Type:</strong> Sélectionnez parmi des options comme avant-première, accès
            anticipé, Q&R, atelier, etc.
          </li>
          <li>
            <strong>Fuseau horaire:</strong> Définissez votre fuseau horaire local.
          </li>
          <li>
            <strong>Date et heure:</strong> Précisez la date, l'heure de début et la durée estimée.
          </li>
          <li>
            <strong>Répétition:</strong> Si l'événement se répète, ajoutez d'autres dates.
          </li>
          <li>
            <strong>Accès:</strong> Décidez si l'événement est gratuit ou exclusif aux membres.
          </li>
        </ul>
        <h3>Définir le fuseau horaire de votre calendrier:</h3>
        <ul>
          <li>Allez dans Calendrier.</li>
          <li>Cliquez sur Fuseau horaire.</li>
          <li>Sélectionnez le fuseau horaire correspondant à votre emplacement.</li>
        </ul>
        <h3>Voir les événements Live Room à venir:</h3>
        <ul>
          <li>Cliquez sur un épisode à venir pour voir:</li>
          <ul>
            <li>Le planning (date et heure).</li>
            <li>Le nombre de membres ayant réservé.</li>
            <li>Si c'est gratuit ou réservé aux membres.</li>
            <li>Le lien vidéo pour l'événement.</li>
          </ul>
        </ul>
        <h3>Comment annuler un événement Live Room:</h3>
        <ul>
          <li>Allez dans Pages et choisissez le Live Room.</li>
          <li>Cliquez sur Modifier la Page.</li>
          <li>
            Sous Schedules, cliquez sur l'icône de suppression pour supprimer l'épisode spécifique.
          </li>
          <li>Les membres ayant réservé l'événement seront informés de l'annulation.</li>
        </ul>
        <p>
          <strong>Astuce:</strong> Si vous devez annuler un épisode Live, il est préférable de le
          reprogrammer et d'envoyer un message personnalisé aux membres de l'audience ayant réservé,
          en les invitant au nouveau programme. Cela permet de maintenir l'engagement et montre que
          vous appréciez leur participation.
        </p>
        <h3>Comment modifier vos épisodes Live Room:</h3>
        <p>
          Pour le moment, vous ne pouvez pas modifier directement les épisodes une fois qu'ils sont
          créés. Pour apporter des modifications, vous devrez supprimer l'épisode existant et en
          programmer un nouveau.
        </p>
        <h3>Suivre et gérer les plannings d'épisodes des créateurs:</h3>
        <p>
          Les Team Admins ont accès à tous les épisodes Live Room des créateurs via un calendrier
          unifié dans le Profil d'Organisation. Cette vue centralisée permet aux admins de gérer
          efficacement les plannings, assurant une coordination fluide. Il est utile de rappeler aux
          créateurs 5 à 10 minutes avant le début de leur épisode. De plus, les admins peuvent gérer
          les annulations, reprogrammer des épisodes et envoyer des messages opportuns aux membres
          de l'audience, garantissant une communication sans faille et des épisodes bien organisés.
        </p>
      </div>
    ),
  },
  {
    key: 'inbox-and-quick-replies',
    label: 'Boîte de réception et réponses rapides',
    topic: CREATOR_DASHBOARD,
    Html: (
      <div>
        <p>
          Les messages personnalisés sont un avantage clé pour les audiences et les membres.
          Lorsqu'une personne réserve ou s'abonne à votre Live Room, un fil de messages automatique
          est créé, vous permettant de communiquer directement avec votre audience.
        </p>
        <h3>Comment envoyer un message à vos membres:</h3>
        <ul>
          <li>Allez dans la Messagerie.</li>
          <li>
            Sélectionnez la personne ou le fil de discussion avec laquelle vous souhaitez échanger.
          </li>
        </ul>
        <h3>Qu'est-ce que le point vert ?</h3>
        <p>
          Le point vert indique que vous êtes disponible en temps réel. Vous pouvez également voir
          si vos membres sont en ligne en repérant le point vert à côté de leur photo de profil.
        </p>
        <h3>Comment créer et envoyer des réponses rapides:</h3>
        <p>
          Gagnez du temps en utilisant des réponses rapides pour les messages courants ou les FAQ.
          Ces modèles peuvent être personnalisés avec des codes courts (par exemple, le nom de
          l'audience, les détails de l'événement, les avantages) pour rendre chaque message plus
          personnel.
        </p>
        <p>
          Si vous co-animez des Live Rooms avec une équipe, tous les membres disposant des
          permissions de gestion peuvent accéder et partager le même ensemble de réponses rapides.
          Ces modèles sont stockés dans la messagerie partagée de votre équipe.
        </p>
        <h3>Pour créer une réponse rapide:</h3>
        <ul>
          <li>Allez dans votre Messagerie.</li>
          <li>Cliquez sur +Nouvelle Réponse.</li>
          <li>Entrez un nom pour identifier le modèle (visible uniquement par votre équipe).</li>
          <li>Saisissez votre message et insérez des codes courts pour le personnaliser.</li>
          <li>Cliquez sur Enregistrer.</li>
        </ul>
      </div>
    ),
  },
  {
    key: 'organization-settings',
    label: "Paramètres de l'organisation",
    topic: CREATOR_DASHBOARD,
    Html: (
      <div>
        <p>
          Lorsque vous créez une page de projet, vous devez la lier à une organisation ou une
          compagnie de production qui possède ou gère le projet.
        </p>
        <p>
          Votre profil d'organisation sert de hub central qui collecte et affiche toutes vos pages
          en un seul endroit. Chaque fois que vous publiez une nouvelle page, les abonnés de votre
          profil recevront des notifications par e-mail ou sur l'application Attractr, vous aidant à
          élargir votre audience et à vous connecter avec les amateurs de théâtre.
        </p>
        <h3>Comment créer un profil d'organisation:</h3>
        <ul>
          <li>Allez dans Paramètres de l'organisation.</li>
          <li>Cliquez sur +Ajouter un profil d'organisation.</li>
          <li>Ajoutez une photo de profil (taille recommandée: 320x320 pixels).</li>
          <li>
            Remplissez les autres informations, telles que le nom de l'organisation, le lien vers le
            site Web, ainsi que la description et les détails de la société.
          </li>
          <li>Cliquez sur Enregistrer.</li>
          <li>
            Si vous êtes en train de soumettre une page de spectacle, cliquez sur Retourner à votre
            page de spectacle pour revenir et finaliser votre soumission.
          </li>
        </ul>
        <h3>Comment ajouter des membres de l’Équipe:</h3>
        <ul>
          <li>Accédez à Paramètres de l’Organisation.</li>
          <li>Cliquez sur Équipe.</li>
          <li>Cliquez sur Ajouter un membre de l’Équipe.</li>
          <li>
            Entrez l'adresse e-mail et sélectionnez le profil d'organisation que vous souhaitez
            attribuer.
          </li>
          <li>Cliquez sur Envoyer l'invitation.</li>
          <li>
            Vos membres d'équipe recevront une invitation par e-mail de la part d'Attractr. Ils
            devront accepter l'invitation pour obtenir l'accès. Toutes les invitations en attente
            apparaîtront dans la section Invitations en attente, où vous pourrez les renvoyer ou les
            annuler.
          </li>
        </ul>
        <h3>Je porte plusieurs casquettes. Puis-je créer plusieurs profils d'organisation ?</h3>
        <p>
          Oui, vous pouvez créer plusieurs profils d'organisation pour gérer différents projets sur
          Attractr. Cependant, lors de la soumission de vos pages, notre équipe peut examiner votre
          association avec l'organisation ou la compagnie de production pour garantir
          l'authenticité. Cela pourrait inclure la vérification des informations via votre domaine
          e-mail, LinkedIn, site Web ou documents d'entreprise.
        </p>
      </div>
    ),
  },
  {
    key: 'insights',
    label: 'Aperçus',
    topic: CREATOR_DASHBOARD,
    Html: (
      <div>
        <p>
          La section Insights fournit des statistiques essentielles pour aider les créateurs à
          suivre leurs performances et l'engagement de leur audience. Les métriques suivantes sont
          actuellement disponibles:
        </p>
        <ul>
          <li>
            Revenus cumulatifs: Suivez les revenus générés par les dons et les abonnements aux Live
            Rooms.
          </li>
          <li>
            Nombre total de membres: Consultez une répartition entre les membres gratuits et
            payants.
          </li>
          <li>
            Vues des épisodes de Live Room: Surveillez le nombre de vues de tous vos épisodes de
            Live Room.
          </li>
          <li>
            Base de données des membres: Accédez à votre liste croissante de membres, y compris
            leurs coordonnées et leur historique d'abonnement.
          </li>
        </ul>
        <p>
          Veuillez noter que les fonctionnalités d'analyse actuelles sont basiques, car nous sommes
          encore en phase de test bêta. Nous développons activement des outils d'analyse plus
          approfondis pour offrir aux compagnies de production une meilleure compréhension du
          comportement de leur audience, des performances de leurs spectacles et du succès de leur
          contenu.
        </p>
      </div>
    ),
  },
  {
    key: 'promote-a-show-while-making-money',
    label: "Promouvoir un spectacle tout en gagnant de l'argent",
    topic: GROW_YOUR_COMMUNITY,
    Html: (
      <div>
        <p>
          Imaginez un théâtre rempli de visages familiers, tous ayant soutenu votre parcours à
          travers le Live Room. Pour les producteurs, les marketeurs et les agents, il est souvent
          difficile d'attirer des talents de haut niveau, de les garder motivés et de réussir
          ensemble. Sans créateurs, il n’y a pas de spectacle. Avec Attractr, les compagnies de
          production peuvent désormais promouvoir leurs spectacles collectivement tout en générant
          des revenus supplémentaires.
        </p>
        <h3>Comment Attractr Vous Aide à Gagner de l'Argent Tout en Promouvant Votre Spectacle:</h3>
        <p>
          Attractr permet aux créateurs d’exploiter leurs talents à la fois sur scène et en dehors.
          En utilisant les Live Rooms, les créateurs peuvent transformer du contenu en coulisses en
          histoires captivantes, favorisant les relations avec leur audience et construisant une
          communauté dévouée. Cela ouvre également la porte à de nouvelles sources de revenus en
          permettant aux créateurs de monétiser du contenu exclusif, de développer des abonnements
          et de collecter des fonds.
        </p>
        <h3>1. Offrez du Contenu Gratuit et Payant:</h3>
        <p>
          Utilisez des épisodes de Live Room gratuits comme introduction à votre communauté,
          permettant à votre audience d'avoir un aperçu de votre univers en coulisses. Ensuite,
          encouragez-les à rejoindre vos abonnements payants pour accéder à du contenu exclusif, des
          aperçus ou des interactions spéciales en direct avec la distribution et l'équipe.
        </p>
        <h3>2. Créez des Abonnements avec Paliers:</h3>
        <p>
          Proposez différents niveaux d'abonnement avec des niveaux d'accès variés. Par exemple:
        </p>
        <ul>
          <li>Palier Basique: Offre un accès aux mises à jour en coulisses et aux aperçus.</li>
          <li>
            Palier Premium: Inclut des interactions en direct, un accès anticipé aux billets et des
            remerciements personnalisés.
          </li>
        </ul>
        <p>
          En diversifiant les options d'abonnement, vous pouvez répondre à une plus grande variété
          d’intérêts de votre audience et augmenter vos sources de revenus.
        </p>
        <h3>3. Engagez et Récompensez Votre Communauté:</h3>
        <p>
          Plus vous interagissez avec votre audience, plus elle devient investie. Organisez
          régulièrement des questions-réponses en direct, des visites des coulisses ou des
          répétitions exclusives pour les maintenir engagés. De plus, récompensez les membres
          fidèles avec des avantages personnalisés, comme des notes de remerciement ou un accès
          anticipé aux billets.
        </p>
        <h3>4. Exploitez la Collaboration:</h3>
        <p>
          Invitez votre distribution et votre équipe à participer aux Live Rooms et à interagir avec
          le public. Leurs perspectives et personnalités uniques aident à élargir votre portée et à
          attirer de nouveaux abonnés au spectacle, tout en promouvant collectivement votre
          production.
        </p>
        <h3>5. Promouvez à Votre Réseau:</h3>
        <p>
          Partagez vos Live Rooms et votre contenu exclusif sur vos réseaux sociaux, vos newsletters
          et au sein de la communauté Attractr. Invitez vos supporters, fans et abonnés actuels à
          vous rejoindre sur Attractr et à vivre le spectacle d’une manière nouvelle et immersive.
          Cela crée un effet boule de neige, encourageant davantage de personnes à participer et,
          potentiellement, à investir dans votre spectacle.
        </p>
        <p>
          Avec Attractr, votre spectacle devient plus qu'une expérience sur scène — il se transforme
          en une activité média durable. En combinant contenu en coulisses et engagement du public,
          vous pouvez promouvoir votre spectacle tout en générant des sources de revenus
          supplémentaires, tout en renforçant votre communauté.
        </p>
      </div>
    ),
  },
  {
    key: 'how-do-payouts-work',
    label: 'Comment fonctionnent les paiements?',
    topic: GETTING_PAID,
    Html: (
      <div>
        <p>Prêt à consulter vos revenus ? Voici un aperçu pour les propriétaires de Live Room:</p>
        <p>
          Votre Paiement = Prix de l’Abonnement - (Frais Attractr (8,5 %) + Frais de traitement de
          paiement (2,9 % + 0,30 $CA))
        </p>
        <h3>Pourquoi votre paiement peut différer des revenus totaux:</h3>
        <p>Plusieurs facteurs peuvent affecter le montant final de votre paiement:</p>
        <ul>
          <li>TVA: La taxe sur la valeur ajoutée peut s’appliquer en fonction de votre région.</li>
          <li>
            Frais de Traitement: Certaines banques peuvent appliquer leurs propres frais ; contactez
            votre institution financière pour plus de détails.
          </li>
          <li>
            Conversion de Devise: Si la devise de votre annonce diffère de celle de votre paiement,
            des frais de change peuvent être appliqués.
          </li>
          <li>
            Frais d’Ajustement: Les remboursements ou annulations peuvent réduire vos revenus.
          </li>
        </ul>
        <p>
          Attractr utilise Stripe Connect pour gérer les paiements, en versant les revenus mensuels
          le 20 de chaque mois. La réception effective des fonds dépend de votre banque ainsi que
          des éventuels jours fériés ou week-ends.
        </p>
      </div>
    ),
  },
  {
    key: 'set-up-a-payout-method',
    label: 'Configurer une méthode de paiement',
    topic: GETTING_PAID,
    Html: (
      <div>
        <p>
          Les créateurs lançant un Live Room doivent configurer une méthode de paiement sur leur
          profil Attractr. Après avoir enregistré votre premier Live Room, vous serez invité à
          ajouter une méthode de paiement. Une fois ajoutée, cette méthode s’appliquera à tous vos
          Live Rooms.
        </p>
        <ul>
          <li>
            Après avoir enregistré votre Live Room, vous serez invité à ajouter une méthode de
            paiement.
          </li>
          <li>Saisissez les détails de votre compte bancaire.</li>
          <li>
            Vous serez redirigé vers les paramètres de Stripe—complétez et soumettez les
            informations requises pour vérifier votre compte.
          </li>
          <li>Une fois vérifié, votre Live Room sera publié.</li>
        </ul>
        <p>
          <strong>Remarque:</strong> Attractr ne publiera pas votre Live Room tant qu'une méthode de
          paiement n’aura pas été configurée.
        </p>
      </div>
    ),
  },
  {
    key: 'fees',
    label: 'Frais',
    topic: GETTING_PAID,
    Html: (
      <div>
        <p>
          Pour continuer à améliorer Attractr en tant que plateforme et solution, nous facturons aux
          Créateurs des frais de service de 10 %. Ces frais sont calculés sur les revenus totaux
          issus des abonnements et des dons et sont automatiquement déduits de votre paiement. Aucun
          frais n'est appliqué aux spectateurs.
        </p>
        <h3>Ce qui est inclus dans les frais Attractr:</h3>
        <ul>
          <li>Frais de paiement (2 $ par mois)</li>
          <li>Coûts des serveurs et d’exploitation</li>
          <li>Programmes d’intégration et d’accompagnement</li>
          <li>Frais de facturation TVA</li>
        </ul>
        <h3>Ce qui n’est pas inclus dans les frais Attractr:</h3>
        <ul>
          <li>Frais de traitement des paiements (2,9 % + 0,30 $CA)</li>
          <li>Frais de conversion de devises (1-2 %)</li>
        </ul>
        <h3>Offres de la plateforme:</h3>
        <ul>
          <li>Chat vidéo en direct illimité (jusqu'à 100 participants)</li>
          <li>Téléchargements de courtes vidéos</li>
          <li>Statistiques (Insights)</li>
          <li>Base de données d’audience</li>
          <li>Messagerie</li>
          <li>Programme pour créateurs et accès à la communauté</li>
          <li>Soutien marketing</li>
        </ul>
        <h3>Revenus du Créateur (Exemple):</h3>
        <ul>
          <li>Montant total des abonnements: $20.00 </li>
          <li>Frais Stripe (2,9 % + 0,30 $): -$0.88 </li>
          <li>Montant net après frais Stripe: $19.12</li>
          <li>Frais de service Attractr (10 %): -$2.00 </li>
          <li>
            <strong>Revenus du Créateur: $17.12</strong>
          </li>
        </ul>
      </div>
    ),
  },
  {
    key: 'vat',
    label: 'VAT',
    topic: GETTING_PAID,
    Html: (
      <div>
        <p>
          Comme Attractr opère à l’échelle mondiale depuis le Canada, les obligations fiscales pour
          les propriétaires de Live Rooms (Créateurs) sont généralement déterminées par la
          localisation de l’acheteur (membres de l’audience).
        </p>
        <h3>Frais de plateforme pour les propriétaires de Live Rooms:</h3>
        <ul>
          <li>
            <strong>Créateurs canadiens:</strong> Si Attractr applique des frais de plateforme ou
            des commissions, la TPS/TVH peut s’appliquer à ces frais.
          </li>
          <li>
            <strong>Créateurs internationaux:</strong> Les taxes sur les frais de plateforme
            s’appliquent généralement uniquement aux fournisseurs basés au Canada, conformément aux
            règles fiscales canadiennes et aux accords internationaux.
          </li>
        </ul>
        <p>
          Il est recommandé aux Créateurs de consulter un professionnel de la fiscalité concernant
          leurs obligations spécifiques, en particulier lorsqu’ils opèrent au-delà des frontières.
        </p>
      </div>
    ),
  },
  {
    key: 'receipts',
    label: 'Reçus',
    topic: GETTING_PAID,
    Html: (
      <div>
        <p>
          Attractr utilise Stripe Connect pour gérer les paiements, garantissant un processus fluide
          et sécurisé pour les Créateurs. Les revenus mensuels sont versés le 20 de chaque mois au
          Créateur de la Live Room. Veuillez noter que l’arrivée effective des fonds dépend des
          délais de traitement de votre banque et peut être retardée en raison des jours fériés ou
          des week-ends. &nbsp; Une fois vos gains traités, vous recevrez une notification de
          paiement d’Attractr, incluant un lien pour télécharger votre reçu.
        </p>
        <h3>Pour consulter votre historique de revenus: </h3>
        <ol>
          <li>Connectez-vous à votre compte Attractr. </li>
          <li>
            Accédez au Tableau de Bord Créateur et cliquez sur Historique des Paiements dans les
            Paramètres.
          </li>
        </ol>
        <h3>Votre historique des revenus affichera les détails suivants: </h3>
        <ul>
          <li>Date de traitement</li>
          <li>Montant perçu</li>
          <li>Frais Stripe</li>
          <li>Frais Attractr</li>
          <li>Revenus totaux</li>
          <li>Live Room ayant généré le paiement</li>
          <li>Type d’achat (ex.: abonnement ou don)</li>
        </ul>
        <p>
          Vous recevrez également un message d’Attractr avec un lien pour télécharger votre reçu de
          paiement.
        </p>
        <h3>Notes Importantes :</h3>
        <ul>
          <li>
            Si votre devise locale est différente de la devise de facturation du Créateur, des
            conversions de devises peuvent apparaître sur votre relevé bancaire.{' '}
          </li>
          <li>
            Pour toute question ou anomalie concernant vos revenus, veuillez contacter le support
            Attractr.
          </li>
        </ul>
      </div>
    ),
  },
];

const articles = {
  en: englishArticles,
  fr: frenchArticles,
};

export const filterArticlesByTopic = (articles, topic) =>
  articles.filter(article => article.topic === topic);

/** The help topics and articles in the given locale. */
export const helpContentFor = locale => ({
  topics: topics[locale] || topics.en,
  articles: articles[locale] || articles.en,
});
